import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import config from '../config.js';
import { Tooltip } from 'bootstrap';
import AmadaImportModal from '../components/AmadaImportModal.jsx';
import { checkUploadSize, uploadFailureMessage } from '../utils/uploadLimits';
import { DuplicateCutSheetModal } from '../components/DuplicateCutSheetForm.jsx';
import { apiErrorMessage } from '../utils/apiError';
import { amadaSizeText, grainCheck } from '../utils/sheetGrain';

/**
 * CutSheetEntryPage (Remediated v4.5)
 * * CHANGES FROM v4.4:
 * - FIXED: "Ghost Switch" bug. Toggling flags, recalculating costs, or changing revisions
 * within a Set no longer kicks the user to Single Sheet view.
 * - ADDED: refreshSheetData() helper to handle state updates context-aware (Set vs Single).
 * - UPDATED: handleToggleFlag, handleCalculateMaterialCosts, and Revision Selector to use refreshSheetData().
 */

function formatHoursAndMinutes(decimalHours) {
  if (!decimalHours || decimalHours <= 0) {
    return "No real data yet";
  }
  const totalSeconds = Math.round(decimalHours * 3600);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${hours}h ${minutes}m ${seconds}s`;
}

// Helper: Parse "HH:MM:SS" to seconds
function parseDurationToSeconds(timeStr) {
  if (!timeStr) return 0;
  const cleanStr = timeStr.trim();
  if (!cleanStr) return 0;
  const parts = cleanStr.split(':').map(Number);
  if (parts.length === 3) return (parts[0] * 3600) + (parts[1] * 60) + parts[2];
  if (parts.length === 2) return (parts[0] * 60) + parts[1];
  if (parts.length === 1) return parts[0];
  return 0;
}

// Helper: Format seconds to "HH:MM:SS"
function formatSecondsToDuration(totalSeconds) {
  if (!totalSeconds && totalSeconds !== 0) return "";
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// Helper: Safe Float Parse (Restored)
function safeParseFloat(strValue) {
    if (!strValue || strValue.toString().trim() === "") return 0;
    const parsed = parseFloat(strValue);
    return isNaN(parsed) || parsed < 0 ? 0 : parsed;
}

// Helper: Parse API errors into user-friendly messages
function parseApiError(error) {
    const message = error?.message || error?.toString() || "Unknown error";

    // Duplicate key violation (PostgreSQL)
    const dupMatch = message.match(/duplicate key.*?detail[:\s]+"?key \(([^)]+)\)=\(([^)]+)\)/i);
    if (dupMatch) {
        const field = dupMatch[1];
        const value = dupMatch[2];
        const fieldNames = {
            'cut_sheet_name': 'Cut Sheet Name',
            'product_id': 'Product ID'
        };
        const friendlyField = fieldNames[field] || field;
        return `A cut sheet with ${friendlyField} '${value}' already exists. Please use a different ${friendlyField}.`;
    }

    // Alternative duplicate format
    if (message.includes('duplicate key') || message.includes('unique constraint')) {
        const nameMatch = message.match(/['"]([\w-]+)['"]/);
        if (nameMatch) {
            return `A cut sheet with this name already exists: '${nameMatch[1]}'. Please choose a different name.`;
        }
        return "A cut sheet with this name already exists. Please choose a different name.";
    }

    // Foreign key violations
    if (message.includes('foreign key') || message.includes('violates foreign key')) {
        if (message.includes('material')) return "The selected material is invalid or no longer exists.";
        if (message.includes('customer')) return "The selected customer is invalid or no longer exists.";
        if (message.includes('part')) return "One or more selected parts are invalid or no longer exist.";
        return "A referenced record is invalid or no longer exists.";
    }

    // Not-null violations
    if (message.includes('not-null') || message.includes('null value')) {
        const fieldMatch = message.match(/column "(\w+)"/);
        if (fieldMatch) {
            const fieldNames = {
                'cut_sheet_name': 'Cut Sheet Name',
                'material_id': 'Material',
                'customer_id': 'Customer',
                'description': 'Description'
            };
            const friendlyField = fieldNames[fieldMatch[1]] || fieldMatch[1];
            return `${friendlyField} is required and cannot be empty.`;
        }
        return "A required field is missing.";
    }

    // Network errors
    if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
        return "Unable to connect to the server. Please check your internet connection and try again.";
    }

    // Server errors
    if (message.includes('500') || message.includes('Internal Server Error')) {
        return "A server error occurred. Please try again or contact support if the issue persists.";
    }

    // Return cleaned message
    return message.length > 200 ? message.substring(0, 200) + "..." : message;
}

// Helper: Check if a time value is filled (for conditional required field styling)
// ─── Cut-sheet packaging recommendation editor (V2, May 2026) ─────────
// Drives the auto/custom override on each cut sheet. Reads the
// effective recommendation from `/cut_sheets/{id}/recommended-packaging`
// and lets the operator move parts between pallets/totes, add/delete
// containers, rename destinations, and save the layout as a
// per-cut-sheet override. Save → PATCH /cut_sheets/{id} with
// `{packaging_override: {...}}`. Reset → PATCH with `{packaging_override: null}`.
const CSPK_PALETTE = [
  '#cfe2ff', '#d1f5e0', '#ffe5b4', '#e8d4ff',
  '#fff3b0', '#c5f3ec', '#ffd1d9', '#e0e0f0',
];

function summarizePalletStacks(stacks) {
  if (!stacks || !stacks.length) return '—';
  if (stacks.length === 1) return `1 stack of ${stacks[0].count}`;
  const buckets = stacks.reduce((acc, s) => {
    acc[s.count] = (acc[s.count] || 0) + 1;
    return acc;
  }, {});
  return Object.entries(buckets)
    .sort((a, b) => Number(b[0]) - Number(a[0]))
    .map(([c, n]) => `${n} stack${n > 1 ? 's' : ''} of ${c}`)
    .join(' + ');
}

function buildOverrideFromRecommendation(rec) {
  // Reshape the live recommendation tree into the override JSON shape
  // expected by the backend. V2 schema (`pallets[].parts: [{part_id,
  // rotated, stacks}]`) — when V3.0 drag UI saves positions, those
  // land in `parts[i].stacks`. Without saved positions, just
  // `{part_id}` is enough; backend falls through to auto-layout.
  const pallets = (rec?.pallets || []).map((p, i) => ({
    id: `p${i + 1}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    destination_kind: p.destination_kind || 'ship',
    destination_label: p.destination,
    parts: (p.parts || []).map(pp => ({ part_id: pp.part_id })),
  }));
  const totes = (rec?.totes || []).map((t, i) => ({
    id: `t${i + 1}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    part_id: t.part_id,
  }));
  return { version: 2, pallets, totes };
}

function CutSheetPackagingEditor({
  cutSheetId, apiBase, isReadOnly, onOverrideSaved,
}) {
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draftOverride, setDraftOverride] = useState(null);
  const [busy, setBusy] = useState(false);
  // V3.3 — operator notes (cut_sheets.packaging_notes). Local draft so
  // the textarea stays editable; persists via PATCH on Save notes.
  const [notesDraft, setNotesDraft] = useState('');
  const [notesSavedValue, setNotesSavedValue] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  const notesDirty = (notesDraft || '').trim() !== (notesSavedValue || '').trim();

  // Map part_id -> part metadata (product_id, description, qty, etc.)
  // built from the recommendation so the editor can show part names
  // without re-fetching.
  const partsById = useMemo(() => {
    const m = {};
    if (!rec) return m;
    for (const pal of (rec.pallets || [])) {
      for (const p of (pal.parts || [])) m[p.part_id] = p;
    }
    for (const t of (rec.totes || [])) {
      m[t.part_id] = {
        part_id: t.part_id,
        product_id: t.product_id,
        description: t.description,
        qty: t.qty,
        size_width: t.size_width,
        size_length: t.size_length,
      };
    }
    return m;
  }, [rec]);

  const reload = React.useCallback(() => {
    if (!cutSheetId) return;
    setLoading(true);
    setError(null);
    fetch(`${apiBase}/cut_sheets/${cutSheetId}/recommended-packaging?sheet_count=1`)
      .then(r => r.ok ? r.json() : Promise.reject(`HTTP ${r.status}`))
      .then(data => {
        setRec(data);
        // Sync notes from server only when the user isn't actively editing
        // them — avoids clobbering their typing on a background refresh.
        const serverNotes = data?.operator_notes || '';
        setNotesSavedValue(serverNotes);
        setNotesDraft(prev => (prev === '' || prev === notesSavedValue) ? serverNotes : prev);
        setLoading(false);
      })
      .catch(e => { setError(String(e)); setLoading(false); });
  // notesSavedValue intentionally omitted — included via the closure;
  // including it would re-fetch every keystroke.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cutSheetId, apiBase]);

  // Save the operator notes via PATCH /cut_sheets/{id}.
  const saveNotes = async () => {
    if (!cutSheetId) return;
    setNotesSaving(true);
    setError(null);
    try {
      const cleanNotes = (notesDraft || '').trim();
      const resp = await fetch(`${apiBase}/cut_sheets/${cutSheetId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ packaging_notes: cleanNotes || null }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setNotesSavedValue(cleanNotes);
      setNotesDraft(cleanNotes);
      reload();
    } catch (e) {
      setError(`Notes save failed: ${e.message || e}`);
    } finally {
      setNotesSaving(false);
    }
  };

  useEffect(() => { reload(); }, [reload]);

  const startEdit = () => {
    setDraftOverride(buildOverrideFromRecommendation(rec));
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraftOverride(null);
    setEditing(false);
  };

  const movePartTo = (partId, targetKind, targetIdx) => {
    // V2 schema: pallets[].parts is `[{part_id, rotated?, stacks?}]`.
    // Moving a part DROPS its saved positions (new pallet → new auto layout).
    setDraftOverride(prev => {
      if (!prev) return prev;
      const next = {
        ...prev,
        pallets: prev.pallets.map(p => ({
          ...p,
          parts: (p.parts || []).filter(pp => pp.part_id !== partId),
        })),
        totes: prev.totes.filter(t => t.part_id !== partId),
      };
      if (targetKind === 'tote') {
        if (targetIdx === -2) {
          next.totes = [...next.totes, { id: `t-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, part_id: partId }];
        } else if (next.totes[targetIdx]) {
          next.totes[targetIdx] = { ...next.totes[targetIdx], part_id: partId };
        }
      } else {
        if (targetIdx === -1) {
          next.pallets = [...next.pallets, {
            id: `p-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            destination_kind: 'ship',
            destination_label: 'Shipping',
            parts: [{ part_id: partId }],
          }];
        } else if (next.pallets[targetIdx]) {
          next.pallets[targetIdx] = {
            ...next.pallets[targetIdx],
            parts: [...(next.pallets[targetIdx].parts || []), { part_id: partId }],
          };
        }
      }
      return next;
    });
  };

  const addEmptyPallet = (kind) => {
    setDraftOverride(prev => ({
      ...prev,
      pallets: [...prev.pallets, {
        id: `p-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        destination_kind: kind,
        destination_label: kind === 'secondary_process' ? 'Brake' : 'Shipping',
        parts: [],
      }],
    }));
  };

  const deletePallet = (idx) => {
    setDraftOverride(prev => {
      const target = prev.pallets[idx];
      const partCount = (target?.parts || []).length;
      if (partCount > 0) {
        if (!window.confirm(`Pallet has ${partCount} part(s). Delete anyway? (Parts will auto-route on next view — fold them into a remaining pallet via Move-to dropdowns instead.)`)) {
          return prev;
        }
      }
      return { ...prev, pallets: prev.pallets.filter((_, i) => i !== idx) };
    });
  };

  // V3.0 — save / clear per-stack positions on a pallet
  // Shared persist helper: takes a fresh draft, sends to backend.
  // Used by both the parent "Save layout" button and the per-pallet
  // "Save positions" action so the operator's drag work commits as
  // soon as they hit Save instead of being silently kept in React
  // state until they remember to hit the outer button.
  const persistDraft = async (nextDraft) => {
    setBusy(true);
    setError(null);
    try {
      const cleaned = {
        ...nextDraft,
        pallets: nextDraft.pallets.filter(p => (p.parts || []).length > 0),
        edited_at: new Date().toISOString(),
      };
      const resp = await fetch(`${apiBase}/cut_sheets/${cutSheetId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ packaging_override: cleaned }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setDraftOverride(nextDraft);  // sync local state with what we just persisted
      reload();
      if (onOverrideSaved) onOverrideSaved();
      return true;
    } catch (e) {
      setError(`Save failed: ${e.message || e}`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  // V3.2 — stack groups (kit nesting). Parts in the same group
  // physically nest in one column on the pallet (Pyro Tower assembly
  // kit pattern: 5 panels stack on top of each other in the same
  // footprint). `setStackGroup(palletIdx, partId, otherPartId)` joins
  // partId into otherPartId's group (or mints a new shared group if
  // neither has one). `clearStackGroup` removes the part from its
  // group. Both update local draft only — operator hits "Save layout"
  // on the parent to persist (or "Save positions" on the SVG editor).
  const setStackGroup = (palletIdx, partId, otherPartId) => {
    setDraftOverride(prev => {
      if (!prev) return prev;
      const pallet = prev.pallets[palletIdx];
      if (!pallet) return prev;
      const otherEntry = (pallet.parts || []).find(pp => pp.part_id === otherPartId);
      const myEntry = (pallet.parts || []).find(pp => pp.part_id === partId);
      let groupId = otherEntry?.stack_group || myEntry?.stack_group;
      if (!groupId) {
        groupId = `g-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      }
      return {
        ...prev,
        pallets: prev.pallets.map((p, i) => i !== palletIdx ? p : {
          ...p,
          parts: (p.parts || []).map(pp => {
            if (pp.part_id === partId || pp.part_id === otherPartId) {
              return { ...pp, stack_group: groupId };
            }
            return pp;
          }),
        }),
      };
    });
  };

  const clearStackGroup = (palletIdx, partId) => {
    setDraftOverride(prev => ({
      ...prev,
      pallets: prev.pallets.map((p, i) => i !== palletIdx ? p : {
        ...p,
        parts: (p.parts || []).map(pp => {
          if (pp.part_id !== partId) return pp;
          const next = { ...pp };
          delete next.stack_group;
          return next;
        }),
      }),
    }));
  };

  const savePalletPositions = async (palletIdx, partLayouts) => {
    // partLayouts: {[part_id]: {rotated, stacks: [{x, y, count}]}}
    // Build the next draft and persist immediately so the operator's
    // drag work doesn't sit unsaved waiting for a separate Save Layout
    // click (which they'd reasonably expect they don't need after
    // clicking the inner "Save positions" button).
    const nextDraft = {
      ...draftOverride,
      pallets: draftOverride.pallets.map((p, i) => i !== palletIdx ? p : {
        ...p,
        parts: (p.parts || []).map(pp => {
          const lo = partLayouts[pp.part_id];
          if (!lo) return pp;
          return {
            part_id: pp.part_id,
            rotated: !!lo.rotated,
            stacks: lo.stacks.map(s => ({
              x: Number(s.x.toFixed(2)),
              y: Number(s.y.toFixed(2)),
              count: Number(s.count),
            })),
          };
        }),
      }),
    };
    await persistDraft(nextDraft);
  };

  const resetPalletPositions = async (palletIdx) => {
    const nextDraft = {
      ...draftOverride,
      pallets: draftOverride.pallets.map((p, i) => i !== palletIdx ? p : {
        ...p,
        parts: (p.parts || []).map(pp => ({ part_id: pp.part_id })),
      }),
    };
    await persistDraft(nextDraft);
  };

  const deleteTote = (idx) => {
    setDraftOverride(prev => ({ ...prev, totes: prev.totes.filter((_, i) => i !== idx) }));
  };

  const renamePallet = (idx, newLabel) => {
    setDraftOverride(prev => ({
      ...prev,
      pallets: prev.pallets.map((p, i) => i === idx ? { ...p, destination_label: newLabel } : p),
    }));
  };

  const setPalletKind = (idx, kind) => {
    setDraftOverride(prev => ({
      ...prev,
      pallets: prev.pallets.map((p, i) => i === idx ? {
        ...p,
        destination_kind: kind,
        destination_label: p.destination_label || (kind === 'secondary_process' ? 'Brake' : 'Shipping'),
      } : p),
    }));
  };

  const saveOverride = async () => {
    if (!draftOverride) return;
    const ok = await persistDraft(draftOverride);
    if (ok) {
      setEditing(false);
      setDraftOverride(null);
    }
  };

  const resetOverride = async () => {
    if (!window.confirm('Reset to auto-recommendation? Current customizations will be lost.')) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await fetch(`${apiBase}/cut_sheets/${cutSheetId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ packaging_override: null }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setEditing(false);
      setDraftOverride(null);
      reload();
      if (onOverrideSaved) onOverrideSaved();
    } catch (e) {
      setError(`Reset failed: ${e.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  if (!cutSheetId) {
    return (
      <div className="text-muted small fst-italic">
        Save the cut sheet first to see the recommended packaging layout.
      </div>
    );
  }
  if (loading) return <div className="text-muted">Loading recommended packaging…</div>;
  if (error && !rec) return <div className="text-danger small">Failed to load: {error}</div>;
  if (!rec) return <div className="text-muted small">No parts on this cut sheet yet.</div>;

  const isCustom = rec.layout_source === 'custom';

  // ── READ-ONLY view ──────────────────────────────────────────────
  if (!editing) {
    return (
      <div>
        <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
          <div className="d-flex align-items-baseline gap-2">
            <span className="small text-muted">Single-sheet recommendation</span>
            <span className={`badge ${isCustom ? 'bg-warning text-dark' : 'bg-secondary'}`}>
              {isCustom ? 'CUSTOM' : 'AUTO'}
            </span>
            {isCustom && rec.override_meta?.edited_at && (
              <span className="small text-muted">
                edited {new Date(rec.override_meta.edited_at).toLocaleString()}
                {rec.override_meta.edited_by ? ` by ${rec.override_meta.edited_by}` : ''}
              </span>
            )}
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={startEdit}
              disabled={isReadOnly || busy}
            >
              <i className="bi bi-pencil me-1"></i>Customize layout
            </button>
            {isCustom && (
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={resetOverride}
                disabled={isReadOnly || busy}
              >
                {busy ? 'Resetting…' : 'Reset to auto'}
              </button>
            )}
          </div>
        </div>
        <PackagingTreeReadOnly rec={rec} />
        {/* V3.3 — operator notes textarea. Free-text that displays on
            the laser kiosk's "Notes" block. Always editable (not gated
            on the Customize layout toggle) so the office can update
            notes without opening the layout editor. */}
        <div className="mt-3">
          <label className="form-label small fw-bold mb-1">
            <i className="bi bi-sticky me-1"></i>
            Operator notes
            <span className="text-muted ms-2 fw-normal">
              (shown on the laser kiosk in the Notes block)
            </span>
          </label>
          <textarea
            className="form-control form-control-sm"
            rows={3}
            value={notesDraft}
            onChange={(e) => setNotesDraft(e.target.value)}
            placeholder="e.g. Pyro Tower kit — stack KC0028-33 vertically as one column. Heavy parts on bottom."
            disabled={isReadOnly || notesSaving}
          />
          <div className="d-flex justify-content-end gap-2 mt-1">
            {notesDirty && (
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setNotesDraft(notesSavedValue)}
                disabled={notesSaving}
              >
                Cancel
              </button>
            )}
            <button
              className="btn btn-sm btn-primary"
              onClick={saveNotes}
              disabled={isReadOnly || notesSaving || !notesDirty}
            >
              {notesSaving ? 'Saving…' : (notesDirty ? 'Save notes' : 'Saved')}
            </button>
          </div>
        </div>
        {(rec.warnings || []).length > 0 && (
          <div className="alert alert-warning py-2 mt-2 small mb-0">
            <div className="fw-bold mb-1">
              <i className="bi bi-info-circle me-1"></i>System diagnostics (not shown on kiosk):
            </div>
            {rec.warnings.map((w, i) => <div key={i}>⚠ {w}</div>)}
          </div>
        )}
      </div>
    );
  }

  // ── EDIT mode ───────────────────────────────────────────────────
  const allPartsOrdered = Object.values(partsById)
    .sort((a, b) => (a.product_id || '').localeCompare(b.product_id || ''));

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <div className="d-flex align-items-baseline gap-2">
          <span className="badge bg-warning text-dark">EDITING</span>
          <span className="small text-muted">
            Move parts between pallets/totes; add/remove containers; rename destinations.
          </span>
        </div>
        <div className="d-flex gap-2">
          <button className="btn btn-sm btn-success" onClick={saveOverride} disabled={busy}>
            {busy ? 'Saving…' : <><i className="bi bi-save me-1"></i>Save layout</>}
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={cancelEdit} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      <div className="d-flex flex-wrap gap-2 mb-2">
        <button className="btn btn-sm btn-outline-warning" onClick={() => addEmptyPallet('secondary_process')} disabled={busy}>
          + Add brake/weld pallet
        </button>
        <button className="btn btn-sm btn-outline-success" onClick={() => addEmptyPallet('ship')} disabled={busy}>
          + Add shipping pallet
        </button>
      </div>

      {/* Editable pallets */}
      {(draftOverride.pallets || []).length === 0 && (draftOverride.totes || []).length === 0 && (
        <div className="alert alert-secondary py-2 small">
          No pallets or totes defined yet. Add a container above and assign parts to it.
        </div>
      )}
      <div className="d-flex flex-column gap-2">
        {(draftOverride.pallets || []).map((pal, idx) => (
          <PalletEditor
            key={pal.id}
            pallet={pal}
            partsById={partsById}
            recPallets={rec?.pallets || []}
            allPartsOrdered={allPartsOrdered}
            currentPalletCount={draftOverride.pallets.length}
            currentToteCount={draftOverride.totes.length}
            paletteIdx={idx}
            onMovePart={movePartTo}
            onDelete={() => deletePallet(idx)}
            onRename={(label) => renamePallet(idx, label)}
            onSetKind={(kind) => setPalletKind(idx, kind)}
            onSavePositions={(layouts) => savePalletPositions(idx, layouts)}
            onResetPositions={() => resetPalletPositions(idx)}
            onSetStackGroup={(partId, otherPartId) => setStackGroup(idx, partId, otherPartId)}
            onClearStackGroup={(partId) => clearStackGroup(idx, partId)}
            disabled={busy}
          />
        ))}
        {(draftOverride.totes || []).map((tote, idx) => (
          <ToteEditor
            key={tote.id}
            tote={tote}
            part={partsById[tote.part_id]}
            currentPalletCount={draftOverride.pallets.length}
            currentToteCount={draftOverride.totes.length}
            onMovePart={movePartTo}
            onDelete={() => deleteTote(idx)}
            disabled={busy}
          />
        ))}
      </div>
    </div>
  );
}

function PackagingTreeReadOnly({ rec }) {
  return (
    <div className="d-flex flex-column gap-2">
      {(rec.pallets || []).map((pal) => (
        <div key={pal.pallet_num} className="card">
          <div className={`card-header d-flex justify-content-between align-items-baseline ${pal.destination_kind === 'ship' ? 'bg-success-subtle' : 'bg-warning-subtle'}`}>
            <div>
              <strong>{pal.label}</strong>
              {pal.auto_routed_review && <span className="badge bg-danger ms-2">Auto-routed — review</span>}
              <span className="badge bg-light text-dark ms-2">Route to: {pal.destination}</span>
            </div>
            <span className="small text-muted">
              {(pal.parts || []).reduce((a, p) => a + (p.qty || 0), 0)} pcs
            </span>
          </div>
          <div className="card-body py-2">
            <table className="table table-sm mb-0">
              <thead>
                <tr><th>Part</th><th>Desc</th><th className="text-end">Qty</th><th className="text-end">Stacks</th></tr>
              </thead>
              <tbody>
                {(pal.parts || []).map((p) => {
                  const role = p.assembly_role;
                  return (
                    <tr key={p.part_id}>
                      <td>
                        <strong>{p.product_id}</strong>
                        {role === 'primary' && <span className="badge bg-success ms-1" title="Assembly primary">PRIMARY</span>}
                        {role === 'component' && <span className="badge bg-info text-dark ms-1" title={`Travels with ${p.assembly_primary_product_id}`}>↪ {p.assembly_primary_product_id}</span>}
                      </td>
                      <td>{p.description || '—'}</td>
                      <td className="text-end font-monospace">{p.qty}</td>
                      <td className="text-end small">{summarizePalletStacks(p.stacks)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {(rec.totes || []).length > 0 && (
        <>
          <div className="small text-muted fw-bold mt-2">TOTES (small parts, one per tote)</div>
          {(rec.totes || []).map((t) => (
            <div key={t.tote_num} className="card">
              <div className="card-header d-flex justify-content-between align-items-baseline bg-info-subtle py-1">
                <strong>{t.label}</strong>
                <span className="small">{t.qty} pcs</span>
              </div>
              <div className="card-body py-2">
                <strong>{t.product_id}</strong>
                {t.description && <span className="text-muted ms-2">{t.description}</span>}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function PalletEditor({
  pallet, partsById, recPallets, allPartsOrdered, currentPalletCount, currentToteCount,
  paletteIdx, onMovePart, onDelete, onRename, onSetKind,
  onSavePositions, onResetPositions,
  onSetStackGroup, onClearStackGroup,
  disabled,
}) {
  const accent = pallet.destination_kind === 'ship' ? 'success' : 'warning';
  // V2 schema: pallet.parts = [{part_id, rotated?, stacks?, stack_group?}]
  // Resolve each entry to its full part metadata for display.
  const partsOnThisPallet = (pallet.parts || [])
    .map(pp => ({ ...partsById[pp.part_id], _override_entry: pp }))
    .filter(p => p.part_id != null);
  const totalQty = partsOnThisPallet.reduce((a, p) => a + (p?.qty || 0), 0);
  const anySaved = partsOnThisPallet.some(p => Array.isArray(p._override_entry?.stacks) && p._override_entry.stacks.length > 0);
  const [showPositions, setShowPositions] = useState(false);

  return (
    <div className="card" style={{ borderColor: CSPK_PALETTE[paletteIdx % CSPK_PALETTE.length], borderWidth: 2 }}>
      <div className={`card-header d-flex flex-wrap justify-content-between align-items-center gap-2 bg-${accent}-subtle`}>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <select
            className="form-select form-select-sm"
            style={{ width: 'auto' }}
            value={pallet.destination_kind}
            onChange={(e) => onSetKind(e.target.value)}
            disabled={disabled}
          >
            <option value="secondary_process">Secondary process (own task)</option>
            <option value="ship">Shipping</option>
          </select>
          <input
            className="form-control form-control-sm"
            style={{ width: 180 }}
            value={pallet.destination_label || ''}
            onChange={(e) => onRename(e.target.value)}
            placeholder="Destination label"
            disabled={disabled}
          />
          <span className="small text-muted">{totalQty} pcs</span>
          {anySaved && (
            <span className="badge bg-info text-dark" title="Operator-positioned stacks saved on this pallet">
              <i className="bi bi-grip-vertical"></i> Custom positions
            </span>
          )}
        </div>
        <div className="d-flex gap-2">
          {partsOnThisPallet.length > 0 && (
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setShowPositions(s => !s)}
              disabled={disabled}
              title="Drag stacks to position them exactly where they should sit on the pallet."
            >
              <i className="bi bi-grip-horizontal me-1"></i>
              {showPositions ? 'Hide positions' : 'Edit positions'}
            </button>
          )}
          <button className="btn btn-sm btn-outline-danger" onClick={onDelete} disabled={disabled}>
            <i className="bi bi-trash"></i> Delete pallet
          </button>
        </div>
      </div>
      <div className="card-body py-2">
        {partsOnThisPallet.length === 0 ? (
          <div className="text-muted small fst-italic">Empty — assign parts via the Move-to dropdowns on other pallets/totes.</div>
        ) : (
          <table className="table table-sm mb-0">
            <thead>
              <tr><th>Part</th><th>Desc</th><th className="text-end">Qty</th><th>Move to…</th></tr>
            </thead>
            <tbody>
              {partsOnThisPallet.map(p => (
                <PartMoveRow
                  key={p.part_id}
                  part={p}
                  currentPalletCount={currentPalletCount}
                  currentToteCount={currentToteCount}
                  onMove={(kind, idx) => onMovePart(p.part_id, kind, idx)}
                  disabled={disabled}
                />
              ))}
            </tbody>
          </table>
        )}
        {showPositions && partsOnThisPallet.length > 0 && (
          <PalletPositionEditor
            pallet={pallet}
            partsOnThisPallet={partsOnThisPallet}
            recPallets={recPallets}
            anySaved={anySaved}
            onSave={(layouts) => { onSavePositions(layouts); setShowPositions(false); }}
            onResetToAuto={() => { onResetPositions(); setShowPositions(false); }}
            onCancel={() => setShowPositions(false)}
            disabled={disabled}
          />
        )}
      </div>
    </div>
  );
}

// V3.0 — drag-to-position editor for stacks on a single pallet.
// Renders an SVG of the pallet (LANDSCAPE, matches backend coords:
// x along palletL=48, y along palletW=40). Each stack on the pallet is
// a draggable colored rect with snap-to-1" grid and bounds clamp.
// Saving captures every stack's (x, y) into the override's
// `pallets[].parts[].stacks` array; auto-layout reverts.
function PalletPositionEditor({
  pallet, partsOnThisPallet, recPallets, anySaved,
  onSave, onResetToAuto, onCancel, disabled,
}) {
  const SCALE = 7; // px per inch
  const PALLET_L = 48;
  const PALLET_W = 40;
  const PAD_LEFT = 22;   // room for length label rotated
  const PAD_BOTTOM = 18; // room for width label

  // Initial positions: pull from the recommendation tree's effective layout
  // for THIS pallet. We need each part's tile_l/tile_w/rotated + current
  // stacks (which may already be saved-positions or auto-shelf-pack output).
  const initialDraft = useMemo(() => {
    const d = {};
    // Find this pallet in the recommendation by matching part membership
    // (override pallet IDs aren't on the rec; the rec is the post-override
    // effective layout, so destination + parts membership is the closest match).
    const myPartIds = new Set(partsOnThisPallet.map(p => p.part_id));
    let recPallet = null;
    let bestOverlap = 0;
    for (const rp of recPallets) {
      const overlap = (rp.parts || []).filter(rpp => myPartIds.has(rpp.part_id)).length;
      if (overlap > bestOverlap) {
        bestOverlap = overlap;
        recPallet = rp;
      }
    }
    if (!recPallet) return d;
    for (const rpp of (recPallet.parts || [])) {
      if (!myPartIds.has(rpp.part_id)) continue;
      d[rpp.part_id] = {
        rotated: !!rpp.rotated,
        tileL: Number(rpp.tile_l) || 0,
        tileW: Number(rpp.tile_w) || 0,
        product_id: rpp.product_id,
        stacks: (rpp.stacks || []).map(s => ({
          x: Number(s.x) || 0,
          y: Number(s.y) || 0,
          count: Number(s.count) || 0,
        })),
      };
    }
    return d;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);  // freeze on first render — operator drags from the current displayed state

  const [draftStacks, setDraftStacks] = useState(initialDraft);
  const [drag, setDrag] = useState(null);
  const svgRef = useRef(null);

  // Mouse drag handlers (window-level so drag continues outside the rect)
  useEffect(() => {
    if (!drag) return;
    const handleMove = (e) => {
      const dxIn = (e.clientX - drag.startMouseX) / SCALE;
      const dyIn = (e.clientY - drag.startMouseY) / SCALE;
      const part = draftStacks[drag.partId];
      if (!part) return;
      const tileL = part.tileL || 0;
      const tileW = part.tileW || 0;
      // Snap to 1" grid; clamp to pallet bounds.
      const newX = Math.round(Math.max(0, Math.min(PALLET_L - tileL, drag.startX + dxIn)));
      const newY = Math.round(Math.max(0, Math.min(PALLET_W - tileW, drag.startY + dyIn)));
      setDraftStacks(prev => ({
        ...prev,
        [drag.partId]: {
          ...prev[drag.partId],
          stacks: prev[drag.partId].stacks.map((s, i) =>
            i === drag.stackIdx ? { ...s, x: newX, y: newY } : s
          ),
        },
      }));
    };
    const handleUp = () => setDrag(null);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [drag, draftStacks]);

  const beginDrag = (e, partId, stackIdx) => {
    if (disabled) return;
    e.preventDefault();
    setDrag({
      partId, stackIdx,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startX: draftStacks[partId].stacks[stackIdx].x,
      startY: draftStacks[partId].stacks[stackIdx].y,
    });
  };

  // Rotate-90° toggles the part's orientation. All stacks of the part
  // rotate together (different rotations per stack of the same part
  // doesn't make physical sense). Tile dims swap; stack positions are
  // clamped to the new bounds so they don't fall off the pallet.
  const rotatePart = (partId) => {
    if (disabled) return;
    setDraftStacks(prev => {
      const part = prev[partId];
      if (!part) return prev;
      const newTileL = Number(part.tileW) || 0;
      const newTileW = Number(part.tileL) || 0;
      return {
        ...prev,
        [partId]: {
          ...part,
          rotated: !part.rotated,
          tileL: newTileL,
          tileW: newTileW,
          stacks: part.stacks.map(s => ({
            ...s,
            x: Math.round(Math.max(0, Math.min(PALLET_L - newTileL, s.x))),
            y: Math.round(Math.max(0, Math.min(PALLET_W - newTileW, s.y))),
          })),
        },
      };
    });
  };

  // Detect overlap / overhang for visual flagging
  const flagsByStack = useMemo(() => {
    const flags = {};
    const rects = [];
    for (const [partId, part] of Object.entries(draftStacks)) {
      part.stacks.forEach((s, si) => {
        const key = `${partId}-${si}`;
        const r = { key, x1: s.x, y1: s.y, x2: s.x + (part.tileL || 0), y2: s.y + (part.tileW || 0) };
        rects.push(r);
        const overhang = r.x2 > PALLET_L + 0.01 || r.y2 > PALLET_W + 0.01;
        flags[key] = { overhang, overlap: false };
      });
    }
    for (let i = 0; i < rects.length; i++) {
      for (let j = i + 1; j < rects.length; j++) {
        const a = rects[i], b = rects[j];
        if (a.x1 < b.x2 - 0.01 && a.x2 > b.x1 + 0.01 && a.y1 < b.y2 - 0.01 && a.y2 > b.y1 + 0.01) {
          flags[a.key].overlap = true;
          flags[b.key].overlap = true;
        }
      }
    }
    return flags;
  }, [draftStacks]);

  const anyConflict = Object.values(flagsByStack).some(f => f.overhang || f.overlap);

  const palletPxW = PALLET_L * SCALE;  // landscape: long axis horizontal
  const palletPxH = PALLET_W * SCALE;
  const svgW = palletPxW + PAD_LEFT + 4;
  const svgH = palletPxH + PAD_BOTTOM + 4;
  const px0 = PAD_LEFT;
  const py0 = 2;

  const partColor = (partId) => {
    const idx = (Number(partId) * 7) % CSPK_PALETTE.length;
    return CSPK_PALETTE[idx];
  };

  return (
    <div className="border-top mt-2 pt-2">
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <small className="text-muted">
          <i className="bi bi-grip-vertical me-1"></i>
          Drag stacks to position. Snap = 1″. Click the <span style={{ display: 'inline-block', border: '1px solid #444', borderRadius: '50%', width: 16, height: 16, textAlign: 'center', lineHeight: '14px', fontSize: 11 }}>↻</span> on a stack to rotate the part 90°. Save commits to the database; Reset to auto clears positions.
        </small>
        <div className="d-flex gap-2">
          {anySaved && (
            <button className="btn btn-sm btn-outline-warning" onClick={onResetToAuto} disabled={disabled}>
              Reset to auto
            </button>
          )}
          <button className="btn btn-sm btn-outline-secondary" onClick={onCancel} disabled={disabled}>
            Cancel
          </button>
          <button
            className="btn btn-sm btn-success"
            onClick={() => onSave(draftStacks)}
            disabled={disabled || anyConflict}
            title={anyConflict ? 'Resolve overlap / overhang before saving' : 'Save these positions to the override'}
          >
            <i className="bi bi-save me-1"></i>Save positions
          </button>
        </div>
      </div>
      {anyConflict && (
        <div className="alert alert-warning py-1 small mb-2">
          ⚠ Some stacks overlap each other or extend past the pallet edge. Adjust before saving.
        </div>
      )}
      <svg
        ref={svgRef}
        width={svgW}
        height={svgH}
        style={{ background: '#fafbfc', border: '1px solid #ced4da', borderRadius: 4 }}
      >
        {/* Pallet base (light theme) */}
        <rect
          x={px0} y={py0}
          width={palletPxW} height={palletPxH}
          fill="#f0e6d2"
          stroke="#8a6f44" strokeWidth={1.2}
        />
        {/* Deck-board lines along the long axis (vertical lines in landscape) */}
        {Array.from({ length: 5 }).map((_, i) => {
          const x = px0 + (i + 1) * (palletPxW / 6);
          return <line key={`db${i}`} x1={x} y1={py0 + 2} x2={x} y2={py0 + palletPxH - 2} stroke="#c4a878" strokeWidth={0.6} />;
        })}

        {/* Stacks */}
        {Object.entries(draftStacks).map(([partId, part]) => {
          const color = partColor(partId);
          return part.stacks.map((s, si) => {
            const px = px0 + s.x * SCALE;
            const py = py0 + s.y * SCALE;
            const rectW = (part.tileL || 0) * SCALE;
            const rectH = (part.tileW || 0) * SCALE;
            const flag = flagsByStack[`${partId}-${si}`] || {};
            const isDragged = drag?.partId === partId && drag?.stackIdx === si;
            const isBad = flag.overhang || flag.overlap;
            return (
              <g key={`${partId}-${si}`} style={{ cursor: isDragged ? 'grabbing' : 'grab' }}>
                <rect
                  x={px} y={py} width={rectW} height={rectH}
                  fill={color}
                  fillOpacity={isDragged ? 0.7 : (isBad ? 0.5 : 0.85)}
                  stroke={isBad ? '#dc3545' : '#222'}
                  strokeWidth={isBad ? 2 : (isDragged ? 2 : 1)}
                  strokeDasharray={isBad ? '4 2' : null}
                  onMouseDown={(e) => beginDrag(e, partId, si)}
                />
                {Math.min(rectW, rectH) >= 18 && (
                  <text
                    x={px + rectW / 2}
                    y={py + rectH / 2 + 4}
                    textAnchor="middle"
                    fontSize="11"
                    fontWeight="bold"
                    fill="#222"
                    pointerEvents="none"
                    style={{ paintOrder: 'stroke', stroke: '#fff', strokeWidth: 2.5, userSelect: 'none' }}
                  >
                    {s.count}
                  </text>
                )}
                {Math.min(rectW, rectH) >= 28 && part.product_id && (
                  <text
                    x={px + rectW / 2}
                    y={py + rectH / 2 - 6}
                    textAnchor="middle"
                    fontSize="9"
                    fill="#444"
                    pointerEvents="none"
                    style={{ paintOrder: 'stroke', stroke: '#fff', strokeWidth: 2, userSelect: 'none' }}
                  >
                    {part.product_id}
                  </text>
                )}
                {/* Rotate-90° button overlay (top-right corner). Only on
                    the first stack of each part to avoid clutter — clicking
                    rotates ALL stacks of this part. Hidden when stack is too
                    small to fit the button. */}
                {si === 0 && Math.min(rectW, rectH) >= 16 && (
                  <g
                    onClick={(e) => { e.stopPropagation(); rotatePart(partId); }}
                    style={{ cursor: 'pointer' }}
                  >
                    <circle
                      cx={px + rectW - 9}
                      cy={py + 9}
                      r={8}
                      fill="#fff"
                      fillOpacity={0.92}
                      stroke="#444"
                      strokeWidth={1}
                    />
                    <text
                      x={px + rectW - 9}
                      y={py + 12}
                      textAnchor="middle"
                      fontSize="11"
                      fontWeight="bold"
                      fill="#444"
                      pointerEvents="none"
                      style={{ userSelect: 'none' }}
                    >
                      ↻
                    </text>
                  </g>
                )}
              </g>
            );
          });
        })}

        {/* Dimension labels */}
        <text x={px0 + palletPxW / 2} y={py0 + palletPxH + 14} textAnchor="middle" fontSize="11" fill="#6c757d" fontFamily="monospace">
          {PALLET_L}″
        </text>
        <text x={10} y={py0 + palletPxH / 2} textAnchor="middle" fontSize="11" fill="#6c757d" fontFamily="monospace"
              transform={`rotate(-90 10 ${py0 + palletPxH / 2})`}>
          {PALLET_W}″
        </text>
      </svg>
    </div>
  );
}

function ToteEditor({ tote, part, currentPalletCount, currentToteCount, onMovePart, onDelete, disabled }) {
  return (
    <div className="card border-info">
      <div className="card-header d-flex justify-content-between align-items-center bg-info-subtle py-1">
        <div className="d-flex align-items-center gap-2">
          <strong>Tote</strong>
          <span className="small text-muted">{part?.qty || 0} pcs</span>
        </div>
        <button className="btn btn-sm btn-outline-danger" onClick={onDelete} disabled={disabled}>
          <i className="bi bi-trash"></i> Delete tote
        </button>
      </div>
      <div className="card-body py-2">
        {part ? (
          <table className="table table-sm mb-0">
            <thead>
              <tr><th>Part</th><th>Desc</th><th className="text-end">Qty</th><th>Move to…</th></tr>
            </thead>
            <tbody>
              <PartMoveRow
                part={part}
                currentPalletCount={currentPalletCount}
                currentToteCount={currentToteCount}
                onMove={(kind, idx) => onMovePart(part.part_id, kind, idx)}
                disabled={disabled}
              />
            </tbody>
          </table>
        ) : (
          <div className="text-muted small">Tote has no part assigned.</div>
        )}
      </div>
    </div>
  );
}

function PartMoveRow({ part, currentPalletCount, currentToteCount, onMove, disabled }) {
  const [target, setTarget] = useState('');
  const handleChange = (e) => {
    const v = e.target.value;
    setTarget('');  // reset
    if (!v) return;
    if (v === 'new-pallet') return onMove('pallet', -1);
    if (v === 'new-tote') return onMove('tote', -2);
    const [k, i] = v.split(':');
    onMove(k, Number(i));
  };
  return (
    <tr>
      <td><strong>{part.product_id}</strong></td>
      <td className="small">{part.description || '—'}</td>
      <td className="text-end font-monospace">{part.qty}</td>
      <td>
        <select
          className="form-select form-select-sm"
          style={{ minWidth: 200 }}
          value={target}
          onChange={handleChange}
          disabled={disabled}
        >
          <option value="">Move to…</option>
          {Array.from({ length: currentPalletCount }, (_, i) => (
            <option key={`p${i}`} value={`pallet:${i}`}>Pallet {i + 1}</option>
          ))}
          <option value="new-pallet">+ New pallet</option>
          {Array.from({ length: currentToteCount }, (_, i) => (
            <option key={`t${i}`} value={`tote:${i}`}>Tote {i + 1}</option>
          ))}
          <option value="new-tote">+ New tote</option>
        </select>
      </td>
    </tr>
  );
}

function hasTimeValue(value) {
    if (!value && value !== 0) return false;
    const str = value.toString().trim();
    if (str === "") return false;
    const seconds = parseDurationToSeconds(str);
    return seconds >= 0;
}

export default function CutSheetEntryPage() {
  const { cutSheetId } = useParams();
  const API_BASE = config.API_BASE_URL;

  // --- LIST VIEW STATE ---
  const [listItems, setListItems] = useState([]); 
  const [searchTerm, setSearchTerm] = useState("");
  const [productFilter, setProductFilter] = useState(""); 
  const [customerFilter, setCustomerFilter] = useState("");
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [showTemporary, setShowTemporary] = useState(false);
  const [showArchivedSheetsInSet, setShowArchivedSheetsInSet] = useState(false);
  const [sortBy, setSortBy] = useState("name_asc");

  // --- SELECTION & MODE STATE ---
  const [selectedItem, setSelectedItem] = useState(null); 
  const [isSetMode, setIsSetMode] = useState(false); 
  const [activeTab, setActiveTab] = useState('overview'); 

  // --- SET MEMORY STATE ---
  const [setHeader, setSetHeader] = useState({ set_name: "", description: "", cut_sheet_set_id: null });
  const [setSheets, setSetSheets] = useState([]);
  const [setLevelActiveStatus, setSetLevelActiveStatus] = useState(true);

  // --- FORM FIELD STATE (Right Panel) ---
  const [dbCutSheetId, setDbCutSheetId] = useState(null);
  // "Copy onto another material" dialog (the twin a pending laser job can switch to, 2026-10-21)
  const [showDuplicate, setShowDuplicate] = useState(false);
  const [cutSheetName, setCutSheetName] = useState("");
  const [description, setDescription] = useState("");
  const [materialId, setMaterialId] = useState("");
  const [activeStatus, setActiveStatus] = useState(true);
  const [notes, setNotes] = useState("");
  const [dropMaterialId, setDropMaterialId] = useState("");
  const [dropQuantity, setDropQuantity] = useState("");
  const [useRemnant, setUseRemnant] = useState(false);
  const [remnantList, setRemnantList] = useState([]);
  // Multi-remnant array. Each entry: { width, length, quantity } as strings
  // (kept as strings so partially-typed input doesn't trigger validation
  // until save). Empty array = no non-inventory remnants on this sheet.
  const [csRemnants, setCsRemnants] = useState([]);
  // Read-only cost-math breakdown from GET /cut_sheets/{id} — how the
  // per-part material costs were derived (price source, remnant credit,
  // area-weighted allocation). Refreshed on every sheet load/save.
  const [costBreakdown, setCostBreakdown] = useState(null);
  const [initialLaserMinutes, setInitialLaserMinutes] = useState("");
  
  // Amada Data
  const [cutPathTime, setCutPathTime] = useState("");
  const [shCutTime, setShCutTime] = useState("");
  const [pierceTime, setPierceTime] = useState("");
  const [totalCutTime, setTotalCutTime] = useState("");
  const [shuttleTime, setShuttleTime] = useState("00:02:40");  // 160s calibrated per-sheet handling (swap+load+align), 2026-08

  const [historicalLaserHours, setHistoricalLaserHours] = useState(0);
  const [customerId, setCustomerId] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [cutSheetParts, setCutSheetParts] = useState([]); // Local state for parts
  
  // Set Specifics
  const [quantityInSet, setQuantityInSet] = useState(1);

  // Flagging
  const [flagged, setFlagged] = useState(false);
  const [flagReason, setFlagReason] = useState("");
  const [flaggedByName, setFlaggedByName] = useState("");

  // Amada multi-sheet import modal
  const [showAmadaImport, setShowAmadaImport] = useState(false);

  // Add Part Inputs
  const [newPartId, setNewPartId] = useState("");
  const [newPartQty, setNewPartQty] = useState("");
  const [partCustomerFilter, setPartCustomerFilter] = useState("");
  const [newPartSearch, setNewPartSearch] = useState("");

  // Dropdown Data
  const [allParts, setAllParts] = useState([]);
  const [allMaterials, setAllMaterials] = useState([]);
  const [allCustomers, setAllCustomers] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [thicknesses, setThicknesses] = useState([]);
  const [surfaceFinishes, setSurfaceFinishes] = useState([]);

  // Material Filters
  const [ownerFilter, setOwnerFilter] = useState("");
  const [materialTypeFilter, setMaterialTypeFilter] = useState("");
  const [thicknessFilter, setThicknessFilter] = useState("");
  const [surfaceFinishFilter, setSurfaceFinishFilter] = useState("");

  // UI / Logic
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [missingSpecWarning, setMissingSpecWarning] = useState(null);
  const [enlargedImage, setEnlargedImage] = useState(null);

  // Versioning
  const [revisionHistory, setRevisionHistory] = useState([]);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [hasObsoleteParts, setHasObsoleteParts] = useState(false);
  const [currentRevisionCode, setCurrentRevisionCode] = useState('A');

  // Unsaved Changes
  const [originalData, setOriginalData] = useState(null);
  // --- STATE FOR UNSAVED CHANGES (SETS) ---
  const [originalSetData, setOriginalSetData] = useState(null);

  // --- COMPUTED VALUES ---

  // Auto-Calc Laser Time based on parts + shuttle time
  const totalCalculatedLaserTime = useMemo(() => {
    if (!cutSheetParts || cutSheetParts.length === 0) {
      return 0;
    }
    const partMinutes = cutSheetParts.reduce((total, part) => {
      const partTime = part.initial_est_laser_minutes || 0;
      const qty = part.quantity_per_sheet || 0;
      return total + (partTime * qty);
    }, 0);
    const shuttleMinutes = parseDurationToSeconds(shuttleTime) / 60;
    return Math.round((partMinutes + shuttleMinutes) * 100) / 100;
  }, [cutSheetParts, shuttleTime]);

  // --- EFFECTS ---

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (cutSheetId) {
        handleSelectSheetById(cutSheetId);
    }
  }, [cutSheetId]);

  // Auto-calculate Total Cut Time for Amada
  useEffect(() => {
    const t1 = parseDurationToSeconds(cutPathTime);
    const t2 = parseDurationToSeconds(shCutTime);
    const t3 = parseDurationToSeconds(pierceTime);
    
    if (t1 > 0 || t2 > 0 || t3 > 0) {
        setTotalCutTime(formatSecondsToDuration(t1 + t2 + t3));
    }
  }, [cutPathTime, shCutTime, pierceTime]);

  // Laser Spec Validation
  useEffect(() => {
    if (!materialId) {
        setMissingSpecWarning(null);
        return;
    }
    const selectedMat = allMaterials.find(m => m.material_id === parseInt(materialId));
    if (selectedMat && selectedMat.material_type && selectedMat.thickness_id) {
        fetch(`${API_BASE}/laser_specs/validate?material_type=${encodeURIComponent(selectedMat.material_type)}&thickness_id=${selectedMat.thickness_id}`)
            .then(res => res.json())
            .then(data => {
                if (!data.exists) {
                    setMissingSpecWarning(`WARNING: Material '${selectedMat.material_code}' has no Laser Tech Spec.`);
                } else {
                    setMissingSpecWarning(null);
                }
            })
            .catch(e => console.error(e));
    } else {
        setMissingSpecWarning(null);
    }
  }, [materialId, allMaterials]);

  // Update Surface Finishes when Material Type changes
  useEffect(() => {
    async function fetchDependentFinishes() {
        const type = materialTypes.find(mt => mt.type_name === materialTypeFilter);
        const typeId = type ? type.material_type_id : null;
        loadSurfaceFinishes(typeId);
    }
    fetchDependentFinishes();
  }, [materialTypeFilter, materialTypes]);

  // Unsaved Changes Warning Logic (Enhanced)
  const isFormDirty = useMemo(() => {
      // 1. SET MODE CHECK
      if (isSetMode) {
          if (!originalSetData) return false;

          // Check Header Changes
          if (setHeader.set_name !== originalSetData.set_name) return true;
          if (setHeader.description !== originalSetData.description) return true;

          // Check Sheet Count Changes
          if (setSheets.length !== originalSetData.sheets.length) return true;

          // Check Deep Changes in Individual Sheets
          const cleanSheetsCurrent = setSheets.map(s => ({
              n: s.cut_sheet_name, d: s.description, m: s.material_id, 
              p: s.parts, t: s.initial_est_laser_minutes 
          }));
          const cleanSheetsOriginal = originalSetData.sheets.map(s => ({
              n: s.cut_sheet_name, d: s.description, m: s.material_id, 
              p: s.parts, t: s.initial_est_laser_minutes 
          }));

          return JSON.stringify(cleanSheetsCurrent) !== JSON.stringify(cleanSheetsOriginal);
      }

      // 2. SINGLE SHEET CHECK (Original Logic)
      if (!originalData) return false;

      const currentData = {
          cutSheetName, description, materialId: materialId || "",
          activeStatus, notes, dropMaterialId: dropMaterialId || "",
          dropQuantity: dropQuantity || "", initialLaserMinutes: initialLaserMinutes || "",
          customerId: customerId || ""
      };
      
      for (const key in currentData) {
          if (currentData[key] !== originalData[key]) return true;
      }
      return false;

  }, [isSetMode, originalSetData, setHeader, setSheets, originalData, cutSheetName, description, materialId, activeStatus, notes, dropMaterialId, dropQuantity, initialLaserMinutes, customerId]);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isFormDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isFormDirty]);

  // When the user toggles "Show archived" OFF while currently viewing an archived
  // sheet, bounce them back to Set Overview — otherwise the tab strip shows no
  // active tab but the form panel below keeps rendering the (now-hidden) archived
  // sheet's data, which looks broken. Triggers only when crossing from "viewing
  // archived" → "archived hidden", never when adding/removing sheets generally.
  useEffect(() => {
    if (showArchivedSheetsInSet) return;
    if (!isSetMode) return;
    if (activeTab === 'overview') return;
    const idx = parseInt(activeTab, 10);
    const current = Number.isFinite(idx) ? setSheets[idx] : null;
    if (current && current.is_current_revision === false) {
      setActiveTab('overview');
    }
  }, [showArchivedSheetsInSet, isSetMode, activeTab, setSheets]);

  // New Cut Sheet Checklist for guided setup
  const newCutSheetChecklist = useMemo(() => {
    // Only show checklist when creating a new cut sheet (not editing existing)
    if (dbCutSheetId || isSetMode) return null;

    const items = [];

    // 1. Upload PDF or fill basic details
    const hasBasicInfo = cutSheetName && description?.trim();
    items.push({
      id: 'upload-pdf',
      label: 'Upload Amada PDF or Enter Details',
      completed: hasBasicInfo && hasTimeValue(cutPathTime),
      section: 'import-card'
    });

    // 2. Sheet Name
    items.push({
      id: 'sheet-name',
      label: 'Enter Cut Sheet Name',
      completed: !!cutSheetName,
      section: 'sheet-details-card'
    });

    // 3. Description
    items.push({
      id: 'description',
      label: 'Enter Description',
      completed: !!description?.trim(),
      section: 'sheet-details-card'
    });

    // 4. Customer
    items.push({
      id: 'customer',
      label: 'Select Customer',
      completed: !!customerId,
      section: 'sheet-details-card'
    });

    // 5. Material
    items.push({
      id: 'material',
      label: 'Select Material',
      completed: !!materialId,
      section: 'material-card'
    });

    // 6. Amada Times
    items.push({
      id: 'amada-times',
      label: 'Enter Amada Cut Times',
      completed: hasTimeValue(cutPathTime) && hasTimeValue(shCutTime) && hasTimeValue(pierceTime),
      section: 'amada-times-card'
    });

    // 7. Add Parts
    items.push({
      id: 'parts',
      label: 'Add at least one Part',
      completed: cutSheetParts.length > 0,
      section: 'parts-card'
    });

    const completedCount = items.filter(i => i.completed).length;
    const totalCount = items.length;
    const progressPercent = Math.round((completedCount / totalCount) * 100);

    return { items, completedCount, totalCount, progressPercent };
  }, [dbCutSheetId, isSetMode, cutSheetName, description, customerId, materialId, cutPathTime, shCutTime, pierceTime, cutSheetParts]);

  // Scroll to section helper
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Flash effect
      element.classList.add('border-primary', 'border-2');
      setTimeout(() => element.classList.remove('border-primary', 'border-2'), 1500);
    }
  };


  // --- DATA LOADING FUNCTIONS ---

  async function loadInitialData() {
    setLoading(true);
    await Promise.all([
        loadListItems(),
        loadAllParts(),
        loadAllMaterials(),
        loadAllCustomers(),
        loadMaterialTypes(),
        loadThicknesses(),
        loadSurfaceFinishes(),
        loadRemnants()
    ]);
    setLoading(false);
  }

  async function loadListItems() {
    try {
        const [sheetsRes, setsRes] = await Promise.all([
            fetch(`${API_BASE}/cut_sheets`),
            fetch(`${API_BASE}/cut_sheet_sets`)
        ]);
        if (!sheetsRes.ok || !setsRes.ok) throw new Error("Failed to load lists");

        const allSheets = await sheetsRes.json();
        const allSets = await setsRes.json();

        // 1. Independent Sheets
        const independentSheets = allSheets.filter(s => s.cut_sheet_set_id === null).map(s => ({
            ...s,
            type: 'sheet',
            display_name: s.cut_sheet_name,
            sort_date: s.created_at || null,
            fallback_id: s.cut_sheet_id
        }));

        // 2. Sets
        const sets = allSets.map(s => ({
            ...s,
            type: 'set',
            display_name: `📦 ${s.set_name}`,
            sort_date: s.created_at || null,
            fallback_id: s.cut_sheet_set_id
        }));

        setListItems([...sets, ...independentSheets]);
    } catch (err) { setError(err.message); }
  }

  async function loadAllParts() {
    const res = await fetch(`${API_BASE}/parts`);
    if(res.ok) setAllParts(await res.json());
  }
  async function loadAllMaterials() {
    const res = await fetch(`${API_BASE}/materials`);
    if(res.ok) setAllMaterials(await res.json());
  }
  async function loadRemnants() {
    const res = await fetch(`${API_BASE}/materials/remnants`);
    if(res.ok) setRemnantList(await res.json());
  }
  async function loadAllCustomers() {
    const res = await fetch(`${API_BASE}/customers`);
    if(res.ok) setAllCustomers((await res.json()).sort((a, b) => a.name.localeCompare(b.name)));
  }
  async function loadMaterialTypes() {
    const res = await fetch(`${API_BASE}/materials/types`);
    if(res.ok) setMaterialTypes(await res.json());
  }
  async function loadThicknesses() {
    const res = await fetch(`${API_BASE}/material_thicknesses`);
    if(res.ok) setThicknesses(await res.json());
  }
  async function loadSurfaceFinishes(typeId = null) {
    let url = `${API_BASE}/materials/finishes`;
    if (typeId) url += `?material_type_id=${typeId}`;
    const res = await fetch(url);
    if(res.ok) setSurfaceFinishes(await res.json());
  }


  // --- TAB & STATE MANAGEMENT ---

  const getCurrentFormData = () => ({
      cut_sheet_id: dbCutSheetId,
      cut_sheet_name: cutSheetName,
      description: description,
      material_id: materialId,
      active_status: activeStatus,
      notes: notes,
      drop_material_id: dropMaterialId,
      drop_quantity: dropQuantity,
      initial_est_laser_minutes: initialLaserMinutes,
      cut_path_time: cutPathTime,
      sh_cut_time: shCutTime,
      pierce_time: pierceTime,
      total_cut_time: totalCutTime,
      shuttle_time: shuttleTime,
      avg_hrs_per_sheet: historicalLaserHours,
      customer_id: customerId,
      image_path: imageUrl,
      parts: cutSheetParts,
      quantity_in_set: quantityInSet,
      flagged, flag_reason: flagReason, flagged_by_name: flaggedByName,
      is_current_revision: !isReadOnly,
      has_obsolete_parts: hasObsoleteParts,
      revision_code: currentRevisionCode
  });

  const loadFormData = (data) => {
      setDbCutSheetId(data.cut_sheet_id || null);
      setCutSheetName(data.cut_sheet_name || "");
      setDescription(data.description || "");
      setMaterialId(data.material_id ? data.material_id.toString() : "");
      // Auto-detect if the material is a remnant
      if (data.material_id) {
        const mat = allMaterials.find(m => m.material_id === data.material_id);
        setUseRemnant(mat?.material_code?.startsWith('REM-') || false);
      } else {
        setUseRemnant(false);
      }
      setActiveStatus(data.active_status !== false);
      setNotes(data.notes || "");
      setDropMaterialId(data.drop_material_id ? data.drop_material_id.toString() : "");
      setDropQuantity(data.drop_quantity || "");
      // Prefer the multi-remnant array. Fall back to the legacy scalar
      // trio so older API responses still hydrate the form correctly.
      if (Array.isArray(data.remnants) && data.remnants.length > 0) {
          setCsRemnants(data.remnants.map(r => ({
              width: r.width != null ? r.width.toString() : "",
              length: r.length != null ? r.length.toString() : "",
              quantity: r.quantity != null ? r.quantity.toString() : "1",
          })));
      } else if (data.remnant_width && data.remnant_length && (data.remnant_quantity || 0) > 0) {
          setCsRemnants([{
              width:    data.remnant_width.toString(),
              length:   data.remnant_length.toString(),
              quantity: data.remnant_quantity.toString(),
          }]);
      } else {
          setCsRemnants([]);
      }
      setCostBreakdown(data.cost_breakdown || null);
      setInitialLaserMinutes(data.initial_est_laser_minutes || "");
      setCutPathTime(data.cut_path_time || "");
      setShCutTime(data.sh_cut_time || "");
      setPierceTime(data.pierce_time || "");
      setTotalCutTime(data.total_cut_time || "");
      setShuttleTime(data.shuttle_time || "00:02:40");
      setHistoricalLaserHours(data.avg_hrs_per_sheet || 0);
      setCustomerId(data.customer_id ? data.customer_id.toString() : "");
      setImageUrl(data.image_path || "");
      setCutSheetParts(data.parts || []);
      setQuantityInSet(data.quantity_in_set || 1);
      setFlagged(!!data.flagged);
      setFlagReason(data.flag_reason || "");
      setFlaggedByName(data.flagged_by_name || "");
      setIsReadOnly(data.is_current_revision === false);
      setHasObsoleteParts(data.has_obsolete_parts || false);
      setCurrentRevisionCode(data.revision_code || 'A');

      if (data.cut_sheet_name) {
          fetch(`${API_BASE}/cut_sheets/${encodeURIComponent(data.cut_sheet_name)}/revisions`)
            .then(r => r.json()).then(d => setRevisionHistory(d)).catch(e => console.error(e));
      } else {
          setRevisionHistory([]);
      }

      setOriginalData(getCurrentFormData());
  };

  const handleTabChange = (newTab) => {
      if (!isSetMode) return;
      
      // 1. SAVE CURRENT TAB TO MEMORY
      if (activeTab === 'overview') {
          setSetHeader(prev => ({ ...prev, set_name: cutSheetName, description: description }));
      } else {
          const index = parseInt(activeTab);
          const currentData = getCurrentFormData();
          setSetSheets(prev => {
              const copy = [...prev];
              if (copy[index]) copy[index] = { ...copy[index], ...currentData };
              return copy;
          });
      }

      setActiveTab(newTab);

      // 2. LOAD NEW TAB FROM MEMORY
      if (newTab === 'overview') {
          setCutSheetName(setHeader.set_name);
          setDescription(setHeader.description);
          setMaterialId(""); 
          setCutSheetParts([]);
          setDbCutSheetId(null);
      } else {
          const index = parseInt(newTab);
          // Note: setSheets is state, so it might not reflect the update from step 1 immediately.
          // However, we are loading a DIFFERENT index, so that's okay.
          const sheetData = setSheets[index];
          
          if (sheetData) {
              loadFormData(sheetData);
          } else {
              // CRITICAL FIX: If no data exists for this index yet (e.g. freshly created set),
              // we MUST force the form to clear. Passing empty object {} forces defaults in loadFormData.
              loadFormData({});
          }
      }
  };


  // --- SELECTION HANDLERS ---

  const handleCreateNewSingle = () => {
    setIsSetMode(false);
    setSelectedItem(null);
    loadFormData({});
  };

  const handleCreateNewSet = () => {
    const countStr = prompt("How many unique cut sheets are in this set?", "2");
    const count = parseInt(countStr);
    if (!count || count < 1) return;

    setIsSetMode(true);
    setSelectedItem(null);
    setSetHeader({ set_name: "New Set", description: "", cut_sheet_set_id: null });
    setSetLevelActiveStatus(true);

    const emptySheets = Array.from({ length: count }).map((_, i) => ({
        cut_sheet_name: `Sheet ${i + 1}`, quantity_in_set: 1, parts: []
    }));
    setSetSheets(emptySheets);

    // Initialize Dirty Check Snapshot
    setOriginalSetData({
        set_name: "New Set",
        description: "",
        active_status: true,
        sheets: JSON.parse(JSON.stringify(emptySheets))
    });

    setActiveTab('overview');
    setCutSheetName("New Set");
    setDescription("");
  };

  const handleAddSheetToSet = () => {
    // Save current tab to memory first (same pattern as handleTabChange)
    if (activeTab === 'overview') {
        setSetHeader(prev => ({ ...prev, set_name: cutSheetName, description: description }));
    } else {
        const index = parseInt(activeTab);
        const currentData = getCurrentFormData();
        setSetSheets(prev => {
            const copy = [...prev];
            if (copy[index]) copy[index] = { ...copy[index], ...currentData };
            return copy;
        });
    }

    // Add a new empty sheet
    const newIndex = setSheets.length;
    const newSheet = { cut_sheet_name: `Sheet ${newIndex + 1}`, quantity_in_set: 1, parts: [] };
    setSetSheets(prev => [...prev, newSheet]);

    // Switch to the new tab and load its (empty) form data
    setActiveTab(newIndex.toString());
    loadFormData(newSheet);
  };

  // --- REFRESH HELPER (NEW v4.5) ---
  // Refreshes a sheet's data without breaking the current view mode (Set vs Single)
  const refreshSheetData = async (id) => {
      setLoading(true);
      try {
          const res = await fetch(`${API_BASE}/cut_sheets/${id}`);
          if (res.ok) {
              const data = await res.json();
              
              if (isSetMode) {
                  // If in Set Mode, update the specific sheet in the array and reload form
                  // We find the index based on activeTab (assuming we are refreshing the active one)
                  if (activeTab !== 'overview') {
                      const idx = parseInt(activeTab);
                      setSetSheets(prev => {
                          const copy = [...prev];
                          copy[idx] = { ...copy[idx], ...data }; // Merge fresh data
                          return copy;
                      });
                      loadFormData(data); // Refresh UI inputs
                  }
              } else {
                  // Standard Single Sheet behavior
                  setSelectedItem({ ...data, type: 'sheet' });
                  loadFormData(data);
              }
          }
      } catch (e) { setError(e.message); }
      setLoading(false);
  };

  const handleSelectSheetById = async (id) => {
      setLoading(true);
      try {
          const res = await fetch(`${API_BASE}/cut_sheets/${id}`);
          if (res.ok) {
              const data = await res.json();
              setIsSetMode(false); // Explicitly enter Single Mode
              setSelectedItem({ ...data, type: 'sheet' });
              loadFormData(data);
          }
      } catch (e) { setError(e.message); }
      setLoading(false);
  };

  const handleSelectItem = async (item) => {
      setSelectedItem(item);
      setError(null);
      setMissingSpecWarning(null);

      if (item.type === 'sheet') {
          handleSelectSheetById(item.cut_sheet_id);
      } else {
          // Load Set
          setIsSetMode(true);
          setLoading(true);
          try {
              // 1. Get the list to identify which sheets belong to this set
              const res = await fetch(`${API_BASE}/cut_sheets?show_all_revisions=true`);
              if (!res.ok) throw new Error("Failed to load sheet list");
              
              const allSheets = await res.json();
              // Filter for sheets in this set
              const summarySheetsInSet = allSheets.filter(s => s.cut_sheet_set_id === item.cut_sheet_set_id);
              
              // 2. FETCH FULL DETAILS for each sheet
              // The summary list is missing critical fields (material costs, est times, detailed part info).
              // We must fetch the specific endpoint for each sheet to get editable data.
              const detailedSheets = await Promise.all(
                  summarySheetsInSet.map(async (summary) => {
                      const detailRes = await fetch(`${API_BASE}/cut_sheets/${summary.cut_sheet_id}`);
                      if (detailRes.ok) {
                          return await detailRes.json();
                      }
                      return summary; // Fallback to summary if detail fetch fails (unlikely)
                  })
              );

              // Sort sheets by name or ID to maintain consistent tab order
              detailedSheets.sort((a, b) => (a.cut_sheet_name || "").localeCompare(b.cut_sheet_name || ""));

              const headerData = {
                  cut_sheet_set_id: item.cut_sheet_set_id,
                  set_name: item.set_name,
                  description: item.description,
                  active_status: item.active_status !== false
              };

              setSetHeader(headerData);
              setSetLevelActiveStatus(item.active_status !== false);
              // Store the DETAILED sheets in state
              setSetSheets(detailedSheets);

              setOriginalSetData({
                  set_name: item.set_name,
                  description: item.description,
                  active_status: item.active_status !== false,
                  sheets: JSON.parse(JSON.stringify(detailedSheets))
              });

              setActiveTab('overview');
              setCutSheetName(item.set_name);
              setDescription(item.description);
          } catch(e) { 
              console.error(e);
              setError(e.message); 
          }
          setLoading(false);
      }
  };


  // --- LOGIC: PARTS (ADD/DELETE) ---

  const handleAddPart = async () => {
    if (!newPartId || !newPartQty) {
        alert("Select part and quantity.");
        return;
    }
    const partObj = allParts.find(p => p.part_id === parseInt(newPartId));
    if (!partObj) return;

    // SCENARIO 1: Existing Saved Sheet (Immediate API Call)
    if (dbCutSheetId && !isSetMode) {
        try {
            const res = await fetch(`${API_BASE}/cut_sheets/${dbCutSheetId}/parts`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ part_id: parseInt(newPartId), quantity_per_sheet: parseInt(newPartQty) })
            });
            if (!res.ok) throw new Error(await apiErrorMessage(res));
            alert("Part added.");
            handleSelectSheetById(dbCutSheetId); 
        } catch (e) { setError(e.message); }
    } 
    // SCENARIO 2: New/Unsaved Sheet OR Set Mode (Local State)
    else {
        const newPart = { 
            part_id: parseInt(newPartId), 
            quantity_per_sheet: parseInt(newPartQty), 
            product_id: partObj.product_id,
            part_description: partObj.description,
            initial_est_laser_minutes: partObj.initial_est_laser_minutes, // Store locally for calculation
            material_cost: partObj.material_cost_component || 0
        };
        setCutSheetParts(prev => [...prev, newPart]);
    }
    
    setNewPartId(""); 
    setNewPartQty("");
    setNewPartSearch("");
  };

  const handleDeletePart = async (partId, index) => {
    // SCENARIO 1: Existing Saved Sheet (Immediate API Call)
    // Only call API if part has a valid DB part_id (unsaved/unmatched parts have null)
    if (dbCutSheetId && !isSetMode && partId) {
        if (!window.confirm("Remove this part?")) return;
        try {
            const res = await fetch(`${API_BASE}/cut_sheets/${dbCutSheetId}/parts/${partId}`, { method: "DELETE" });
            if (!res.ok) throw new Error(await apiErrorMessage(res));
            handleSelectSheetById(dbCutSheetId);
        } catch(e) { setError(e.message); }
    }
    // SCENARIO 2: New/Unsaved, Set Mode, or part not yet saved to DB (Local State)
    else {
        if (!window.confirm("Remove this part?")) return;
        setCutSheetParts(prev => prev.filter((_, i) => i !== index));
    }
  };


  // --- LOGIC: RESTORED FEATURES (FIXED FOR SET MODE) ---

  const handleCalculateMaterialCosts = async () => {
    if (!dbCutSheetId || !window.confirm("Recalculate material costs for all parts?")) return;
    setLoading(true);
    try {
        const res = await fetch(`${API_BASE}/cut_sheets/${dbCutSheetId}/calculate_material_costs`, { method: "POST" });
        if (!res.ok) throw new Error("Calculation failed");
        alert("Costs updated.");
        // FIX: Use refreshSheetData to stay in Set Mode if applicable
        refreshSheetData(dbCutSheetId);
    } catch(e) { setError(e.message); }
    setLoading(false);
  };

  const handleCreateRevision = async () => {
      if (!dbCutSheetId) return;
      if (!window.confirm("Create new Revision? This snapshots current sheet.")) return;
      try {
          const res = await fetch(`${API_BASE}/cut_sheets/${dbCutSheetId}/create_revision`, { method: "POST" });
          if (!res.ok) throw new Error(await apiErrorMessage(res));
          const data = await res.json();
          alert(data.message);
          
          if (isSetMode) {
              // Special handling for Sets: If we revise a sheet in a set,
              // ideally we reload the set to see the new ID.
              // For now, let's just reload the list items and switch to the new ID within the set tabs
              // by updating the array.
              const idx = parseInt(activeTab);
              setSetSheets(prev => {
                  const copy = [...prev];
                  // Update ID to new revision
                  copy[idx] = { ...copy[idx], cut_sheet_id: data.new_cut_sheet_id }; 
                  return copy;
              });
              // Refresh data for the NEW ID
              refreshSheetData(data.new_cut_sheet_id);
          } else {
              loadListItems();
              handleSelectSheetById(data.new_cut_sheet_id);
          }
      } catch(e) { setError(e.message); }
  };

  const handleToggleFlag = async (e) => {
      const newVal = e.target.checked;
      if (!dbCutSheetId) { 
          // If unsaved, just toggle local state
          setFlagged(newVal); 
          return; 
      }
      
      // If saved, use API logic
      if (newVal) {
          const reason = prompt("Enter flag reason:");
          const name = prompt("Enter your name:");
          if (reason && name) {
              const payload = { flagged: true, flag_reason: reason, flagged_by_name: name, flagged_at: new Date().toISOString() };
              await fetch(`${API_BASE}/cut_sheets/${dbCutSheetId}`, { method: 'PATCH', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(payload) });
              // FIX: Use refreshSheetData
              refreshSheetData(dbCutSheetId);
          }
      } else {
          if (window.confirm("Clear flag?")) {
              await fetch(`${API_BASE}/cut_sheets/${dbCutSheetId}`, { method: 'PATCH', headers: {'Content-Type': 'application/json'}, body: JSON.stringify({ flagged: false }) });
              // FIX: Use refreshSheetData
              refreshSheetData(dbCutSheetId);
          }
      }
  };

  // --- IMAGES ---

  const handlePaste = (e) => {
      if (e.clipboardData?.files?.length) {
          const file = e.clipboardData.files[0];
          const img = new Image();
          img.onload = () => {
              if (img.width > 3000 || img.height > 3000) {
                  alert("Image too large (>3000px). Please resize.");
                  return;
              }
              setUploadFile(file);
          };
          img.src = URL.createObjectURL(file);
      }
  };

  const handleUploadImage = async () => {
      if (!dbCutSheetId || !uploadFile) { alert("Save sheet first."); return; }
      const tooBig = checkUploadSize(uploadFile, "image");
      if (tooBig) { setError(tooBig); return; }
      const fd = new FormData();
      fd.append("file", uploadFile);
      try {
          const res = await fetch(`${API_BASE}/cut_sheets/${dbCutSheetId}/upload-image`, { method: "POST", body: fd });
          if(res.ok) {
              const d = await res.json();
              setImageUrl(d.image_url);
              setUploadFile(null);
              alert("Uploaded!");
          }
      } catch(e) {
          const msg = String(e?.message || e);
          setError(msg.includes('Failed to fetch') ? uploadFailureMessage(uploadFile) : msg);
      }
  };

  // --- SAVE ACTIONS (CORRECTED) ---

  const handleSaveSingle = async () => {
      // 1. --- VALIDATION ---
      const errors = [];

      if (!cutSheetName) errors.push("Name is required");
      if (!description || !description.trim()) errors.push("Description is required");
      if (!materialId) errors.push("Material is required");
      if (!customerId) errors.push("Customer is required for single sheets");

      // Calculate Laser Time
      const laserTimeToSend = totalCalculatedLaserTime > 0 ? totalCalculatedLaserTime : safeParseFloat(initialLaserMinutes);

      if (laserTimeToSend <= 0) {
          errors.push("Estimated Laser Time must be greater than 0");
      }

      // Amada time fields validation
      if (!hasTimeValue(cutPathTime)) errors.push("Cut Path Time is required");
      if (!hasTimeValue(shCutTime)) errors.push("SH Cut Time is required");
      if (!hasTimeValue(pierceTime)) errors.push("Pierce Time is required");
      if (!hasTimeValue(totalCutTime)) errors.push("Total Cut Time is required");

      // Parts validation
      if (!cutSheetParts || cutSheetParts.length === 0) {
          errors.push("At least one part must be added");
      }

      if (errors.length > 0) {
          setError(errors.join("\n• "));
          return;
      }

      // 2. --- PAYLOAD CONSTRUCTION ---
      const payload = {
          ...getCurrentFormData(),
          // Explicit Type Conversion
          material_id: parseInt(materialId),
          customer_id: parseInt(customerId),
          drop_material_id: dropMaterialId ? parseInt(dropMaterialId) : null,
          drop_quantity: dropQuantity ? parseInt(dropQuantity) : 0,
          // Multi-remnant array — drop incomplete rows so a half-typed
          // entry doesn't 422 on Pydantic validation. The API mirrors the
          // primary into the legacy scalar columns automatically.
          remnants: csRemnants
              .map(r => ({
                  width:    parseFloat(r.width),
                  length:   parseFloat(r.length),
                  quantity: parseInt(r.quantity, 10),
              }))
              .filter(r => Number.isFinite(r.width) && r.width > 0
                        && Number.isFinite(r.length) && r.length > 0
                        && Number.isFinite(r.quantity) && r.quantity >= 1),
          initial_est_laser_minutes: laserTimeToSend,
          
          // FIX: Clean the parts array to match backend schema strictly
          parts: cutSheetParts.map(p => ({
              part_id: p.part_id,
              quantity_per_sheet: p.quantity_per_sheet
          })).filter(p => p.part_id) 
      };
      
      let url = dbCutSheetId ? `${API_BASE}/cut_sheets/${dbCutSheetId}` : `${API_BASE}/cut_sheets`;
      let method = dbCutSheetId ? 'PATCH' : 'POST';

      setLoading(true);
      try {
          const res = await fetch(url, {
              method,
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload)
          });
          
          if (!res.ok) {
              throw new Error(await apiErrorMessage(res));
          }

          const d = await res.json();
          const newCutSheetId = d.cut_sheet_id;

          alert("Saved!");

          if(method === 'POST') {
              setDbCutSheetId(newCutSheetId);
              // If it's a new record, reload it to get server fields (timestamps, etc.)
              handleSelectSheetById(newCutSheetId);
          } else {
              // FIX: Update the "Clean State" baseline so the Unsaved Changes warning clears
              // We mimic the structure of the payload we just sent/confirmed
              setOriginalData({
                  ...getCurrentFormData(),
                  initial_est_laser_minutes: laserTimeToSend,
                  cut_sheet_id: dbCutSheetId
              });
              // Reload list to update titles/descriptions in the sidebar
              loadListItems();

              // Refresh the Material Cost Math panel — the server re-ran the
              // cost recalc on save (parts/remnants/material changes), so
              // re-fetch just the breakdown. Non-fatal.
              try {
                  const bdRes = await fetch(`${API_BASE}/cut_sheets/${dbCutSheetId}`);
                  if (bdRes.ok) {
                      const fresh = await bdRes.json();
                      setCostBreakdown(fresh.cost_breakdown || null);
                  }
              } catch (_e) { /* panel refreshes on next sheet load */ }
          }

      } catch(e) {
          setError(parseApiError(e));
      }
      setLoading(false);
  };

  const handleSaveSet = async () => {
      // 1. --- VALIDATION ---
      // If we are on the overview tab, we must have a Set Name.
      // If we are on a specific sheet tab, we use the header name or the local name.
      if (!cutSheetName && activeTab === 'overview') return alert("Set Name required");
      if (!setHeader.set_name && activeTab !== 'overview') return alert("Set Name required");

      const effectiveSetName = activeTab === 'overview' ? cutSheetName : setHeader.set_name;
      const effectiveDescription = activeTab === 'overview' ? description : setHeader.description;

      // 2. --- SYNC ACTIVE TAB TO MEMORY ---
      // We need to capture the latest edits from the active form into our sheets array
      // before we process them.
      let sheetsToProcess = [...setSheets]; // Copy the current state

      if (activeTab !== 'overview') {
          const idx = parseInt(activeTab);
          // Merge current form data into the array at the active index
          sheetsToProcess[idx] = { ...sheetsToProcess[idx], ...getCurrentFormData() };
      } else {
          // If on overview, just sync header info
          setSetHeader(prev => ({ ...prev, set_name: cutSheetName, description: description }));
      }

      // 3. --- AUTO-RENAME LOGIC ---
      // Prevent DB unique constraint errors by renaming generic "Sheet X" to "Set Name - Sheet X"
      sheetsToProcess = sheetsToProcess.map(s => {
          if (s.cut_sheet_name && s.cut_sheet_name.match(/^Sheet \d+$/)) {
              return { ...s, cut_sheet_name: `${effectiveSetName} - ${s.cut_sheet_name}` };
          }
          return s;
      });

      // Update state immediately so UI reflects the new unique names
      setSetSheets(sheetsToProcess);

      setLoading(true);

      try {
          // 4. --- CREATE/GET SET HEADER ---
          let setId = setHeader.cut_sheet_set_id;
          
          if (!setId) {
              const sRes = await fetch(`${API_BASE}/cut_sheet_sets`, {
                  method: 'POST',
                  headers: {'Content-Type': 'application/json'},
                  body: JSON.stringify({
                      set_name: effectiveSetName,
                      description: effectiveDescription
                  })
              });
              if (!sRes.ok) throw new Error("Failed to create Set Header");
              const sData = await sRes.json();
              setId = sData.cut_sheet_set_id;

              setSetHeader(prev => ({ ...prev, cut_sheet_set_id: setId, set_name: effectiveSetName }));
          } else {
              // UPDATE existing set header (name, description, active_status)
              const patchRes = await fetch(`${API_BASE}/cut_sheet_sets/${setId}`, {
                  method: 'PATCH',
                  headers: {'Content-Type': 'application/json'},
                  body: JSON.stringify({
                      set_name: effectiveSetName,
                      description: effectiveDescription,
                      active_status: setLevelActiveStatus
                  })
              });
              if (!patchRes.ok) throw new Error("Failed to update Set Header");
          }

          // 5. --- ITERATE AND SAVE SHEETS ---
          // We map over sheetsToProcess to save them one by one
          const finalSheets = [...sheetsToProcess];

          for(let i = 0; i < finalSheets.length; i++) {
              const sheet = finalSheets[i];
              const sheetLabel = `Sheet ${i+1} (${sheet.cut_sheet_name || 'Unnamed'})`;

              // Enhanced validation for each sheet
              if (!sheet.material_id) throw new Error(`${sheetLabel} is missing a material.`);

              // Calculate Laser Time (parts + shuttle)
              const partsTime = (sheet.parts || []).reduce((acc, p) => acc + ((p.initial_est_laser_minutes||0) * (p.quantity_per_sheet||0)), 0);
              const shuttleMinutes = parseDurationToSeconds(sheet.shuttle_time || "00:02:40") / 60;
              const calculatedTime = partsTime > 0 ? partsTime + shuttleMinutes : 0;
              const manualTime = safeParseFloat(sheet.initial_est_laser_minutes);
              const laserTimeToSend = calculatedTime > 0 ? calculatedTime : manualTime;

              if (laserTimeToSend <= 0) {
                  throw new Error(`${sheetLabel} has 0 Estimated Laser Time. Add parts or enter manually.`);
              }

              // Validate Amada time fields
              if (!hasTimeValue(sheet.cut_path_time)) throw new Error(`${sheetLabel} is missing Cut Path Time.`);
              if (!hasTimeValue(sheet.sh_cut_time)) throw new Error(`${sheetLabel} is missing SH Cut Time.`);
              if (!hasTimeValue(sheet.pierce_time)) throw new Error(`${sheetLabel} is missing Pierce Time.`);
              if (!hasTimeValue(sheet.total_cut_time)) throw new Error(`${sheetLabel} is missing Total Cut Time.`);

              // Validate parts
              if (!sheet.parts || sheet.parts.length === 0) {
                  throw new Error(`${sheetLabel} must have at least one part.`);
              }

              const payload = {
                  ...sheet,
                  material_id: parseInt(sheet.material_id),
                  customer_id: sheet.customer_id ? parseInt(sheet.customer_id) : null,
                  drop_material_id: sheet.drop_material_id ? parseInt(sheet.drop_material_id) : null,
                  drop_quantity: sheet.drop_quantity ? parseInt(sheet.drop_quantity) : 0,
                  // Multi-remnant array — forward whatever the AmadaImportModal
                  // emitted, dropping incomplete rows. Legacy scalar fields
                  // below are retained for any importer that still sends them;
                  // the API mirrors them in.
                  remnants: Array.isArray(sheet.remnants)
                      ? sheet.remnants
                          .map(r => ({
                              width:    parseFloat(r.width),
                              length:   parseFloat(r.length),
                              quantity: parseInt(r.quantity, 10),
                          }))
                          .filter(r => Number.isFinite(r.width) && r.width > 0
                                    && Number.isFinite(r.length) && r.length > 0
                                    && Number.isFinite(r.quantity) && r.quantity >= 1)
                      : undefined,
                  remnant_width: sheet.remnant_width ? parseFloat(sheet.remnant_width) : null,
                  remnant_length: sheet.remnant_length ? parseFloat(sheet.remnant_length) : null,
                  remnant_quantity: sheet.remnant_quantity ? parseInt(sheet.remnant_quantity) : 0,

                  // Clean time fields
                  cut_path_time: sheet.cut_path_time || null,
                  sh_cut_time: sheet.sh_cut_time || null,
                  pierce_time: sheet.pierce_time || null,
                  total_cut_time: sheet.total_cut_time || null,
                  
                  cut_sheet_set_id: setId,
                  quantity_in_set: sheet.quantity_in_set || 1,
                  initial_est_laser_minutes: laserTimeToSend,
                  
                  // Ensure parts are valid
                  parts: (sheet.parts || []).map(p => ({
                      part_id: p.part_id,
                      quantity_per_sheet: p.quantity_per_sheet
                  })).filter(p => p.part_id)
              };

              let url = sheet.cut_sheet_id ? `${API_BASE}/cut_sheets/${sheet.cut_sheet_id}` : `${API_BASE}/cut_sheets`;
              let method = sheet.cut_sheet_id ? 'PATCH' : 'POST';

              // NOTE: Your backend handles part synchronization on PATCH if 'parts' is included,
              // so we DO NOT delete payload.parts here.

              const res = await fetch(url, {
                  method,
                  headers: {'Content-Type': 'application/json'},
                  body: JSON.stringify(payload)
              });

              if(!res.ok) throw new Error(`Failed to save ${sheet.cut_sheet_name}: ${await res.text()}`);
              
              if(method === 'POST') {
                  const d = await res.json();
                  finalSheets[i].cut_sheet_id = d.cut_sheet_id; 
              }
          }

          // 6. --- REFETCH FRESH DATA (The Fix) ---
          // We must reload the sheets from the server to capture server-side calculations
          // (like material_cost) that happened during the save.
          const refreshedSheets = await Promise.all(finalSheets.map(async (s) => {
              // If it's a newly created sheet, it has an ID now. If it was existing, it has an ID.
              if (!s.cut_sheet_id) return s; 
              
              const res = await fetch(`${API_BASE}/cut_sheets/${s.cut_sheet_id}`);
              if (res.ok) {
                  return await res.json();
              }
              return s;
          }));

          setSetSheets(refreshedSheets);
          
          // Update the "Clean State" snapshot using the FRESH data
          setOriginalSetData({
              set_name: effectiveSetName,
              description: effectiveDescription,
              active_status: setLevelActiveStatus,
              sheets: JSON.parse(JSON.stringify(refreshedSheets))
          });
          
          // Update active tab data if we are looking at a specific sheet
          if (activeTab !== 'overview') {
               const idx = parseInt(activeTab);
               if (refreshedSheets[idx]) {
                   setDbCutSheetId(refreshedSheets[idx].cut_sheet_id);
                   setOriginalData(refreshedSheets[idx]); 
                   loadFormData(refreshedSheets[idx]); // Force the form to repaint with new costs
               }
          }

          alert("Set Saved Successfully!");
          loadListItems();

      } catch(e) {
          console.error(e);
          setError(parseApiError(e));
      } finally {
          setLoading(false);
      }
  };

  const handleTimeBlur = (setter, value) => {
      const seconds = parseDurationToSeconds(value);
      if (seconds >= 0 && value.toString().trim() !== "") {
          setter(formatSecondsToDuration(seconds));
      }
  };


  // --- FILTERS ---
  const filteredList = useMemo(() => {
      return listItems.filter(item => {
          if (!showInactive && item.active_status === false) return false;
          if (showFlaggedOnly && !item.flagged) return false;
          if (!showTemporary && item.is_temporary) return false;
          
          const term = searchTerm.toLowerCase();
          const name = (item.display_name || "").toLowerCase();
          const desc = (item.description || "").toLowerCase();
          if (!name.includes(term) && !desc.includes(term)) return false;

          if (customerFilter && item.type === 'sheet' && item.customer_id !== parseInt(customerFilter)) return false;

          if (productFilter && item.type === 'sheet' && item.parts) {
              const pFilter = productFilter.toLowerCase();
              const hasPart = item.parts.some(p => (p.product_id || "").toLowerCase().includes(pFilter));
              if (!hasPart) return false;
          }

          return true;
      }).sort((a,b) => {
          if (sortBy === 'name_asc') return a.display_name.localeCompare(b.display_name);
          if (sortBy === 'name_desc') return b.display_name.localeCompare(a.display_name);
          
          // Date Logic with ID Fallback
          const dateA = a.sort_date ? new Date(a.sort_date).getTime() : 0;
          const dateB = b.sort_date ? new Date(b.sort_date).getTime() : 0;

          if (dateA !== dateB) {
              return sortBy === 'date_newest' 
                  ? dateB - dateA 
                  : dateA - dateB;
          }

          const idA = a.fallback_id || 0;
          const idB = b.fallback_id || 0;
          
          return sortBy === 'date_newest' 
              ? idB - idA 
              : idA - idB;
      });
  }, [listItems, searchTerm, showInactive, showFlaggedOnly, showTemporary, customerFilter, productFilter, sortBy]);

  const filteredPartsForDropdown = useMemo(() => {
      if (!partCustomerFilter) return allParts;
      return allParts.filter(p => p.customer_id === parseInt(partCustomerFilter));
  }, [allParts, partCustomerFilter]);

  const filteredMaterials = useMemo(() => {
      return allMaterials.filter(m => {
          if (ownerFilter && m.owner_customer_id !== parseInt(ownerFilter)) return false;
          if (materialTypeFilter && m.material_type !== materialTypeFilter) return false;
          if (thicknessFilter && m.thickness_id !== parseInt(thicknessFilter)) return false;
          if (surfaceFinishFilter && m.surface_finish !== surfaceFinishFilter) return false;
          return true;
      });
  }, [allMaterials, ownerFilter, materialTypeFilter, thicknessFilter, surfaceFinishFilter]);

  const filteredRemnants = useMemo(() => {
      return remnantList.filter(r => {
          if (r.available_quantity <= 0) return false;
          if (ownerFilter && r.owner_customer_id !== parseInt(ownerFilter)) return false;
          if (materialTypeFilter && r.material_type !== materialTypeFilter) return false;
          if (thicknessFilter && r.thickness_id !== parseInt(thicknessFilter)) return false;
          if (surfaceFinishFilter && r.surface_finish !== surfaceFinishFilter) return false;
          return true;
      });
  }, [remnantList, ownerFilter, materialTypeFilter, thicknessFilter, surfaceFinishFilter]);

  const availableThicknesses = useMemo(() => {
    if (!materialTypeFilter) return thicknesses;
    return thicknesses.filter(t => t.material_type === materialTypeFilter);
  }, [thicknesses, materialTypeFilter]);


  // --- RENDER ---

  return (
    <div className="d-flex" style={{ height: "100vh" }}>
      {/* LEFT PANEL */}
      <div className="border-end p-3 bg-light flex-shrink-0" style={{ width: "450px", overflowY: "auto" }}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Cut Sheets</h5>
          <div className="btn-group">
            <button className="btn btn-sm btn-outline-primary" onClick={handleCreateNewSingle}>+ Single</button>
            <button className="btn btn-sm btn-outline-dark" onClick={handleCreateNewSet}>+ Set</button>
            <button
                className="btn btn-sm btn-outline-info"
                onClick={() => setShowAmadaImport(true)}
                title="Import a multi-sheet Amada Sheet Report PDF"
            >
                <i className="bi bi-file-earmark-pdf me-1"></i>
                Import PDF
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-2 p-2 bg-white border rounded">
            <input className="form-control form-control-sm mb-1" placeholder="Search Name..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
            <input className="form-control form-control-sm mb-1" placeholder="Search Part ID..." value={productFilter} onChange={e => setProductFilter(e.target.value)} />
            <div className="d-flex gap-1 mb-1">
                <select className="form-select form-select-sm" value={customerFilter} onChange={e => setCustomerFilter(e.target.value)}>
                    <option value="">All Customers</option>
                    {allCustomers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
                </select>
                <select className="form-select form-select-sm" value={sortBy} onChange={e => setSortBy(e.target.value)}>
                    <option value="name_asc">Name (A-Z)</option>
                    <option value="name_desc">Name (Z-A)</option>
                    <option value="date_newest">Newest First</option>
                    <option value="date_oldest">Oldest First</option>
                </select>
            </div>
            <div className="form-check form-check-inline small">
                <input className="form-check-input" type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
                <label className="form-check-label">Inactive</label>
            </div>
            <div className="form-check form-check-inline small">
                <input className="form-check-input" type="checkbox" checked={showFlaggedOnly} onChange={e => setShowFlaggedOnly(e.target.checked)} />
                <label className="form-check-label">Flagged</label>
            </div>
            <div className="form-check form-check-inline small">
                <input className="form-check-input" type="checkbox" checked={showTemporary} onChange={e => setShowTemporary(e.target.checked)} />
                <label className="form-check-label">Temporary</label>
            </div>
        </div>

        <div className="list-group">
            {filteredList.map((item, idx) => (
                <button 
                    key={`${item.type}-${item.cut_sheet_id || item.cut_sheet_set_id}-${idx}`}
                    className={`list-group-item list-group-item-action ${
                        (item.type === 'sheet' && item.cut_sheet_id === dbCutSheetId && !isSetMode) || 
                        (item.type === 'set' && item.cut_sheet_set_id === setHeader.cut_sheet_set_id && isSetMode) 
                        ? "active" : ""
                    }`}
                    onClick={() => handleSelectItem(item)}
                >
                    <div className="d-flex w-100 justify-content-between">
                        <h6 className="mb-1">{item.display_name}</h6>
                        {item.type === 'sheet' && <small>Rev {item.revision_code}</small>}
                    </div>
                    {/* [Inactive] Visual Indicator — shown for both sheets and sets */}
                    {item.active_status === false && <div className="text-secondary small fw-bold mb-1">[Inactive]</div>}
                    {item.has_obsolete_parts && <div className="text-danger small fw-bold">⚠ Obsolete Parts</div>}
                </button>
            ))}
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div className="flex-grow-1 d-flex flex-column overflow-hidden">
        {isSetMode && (() => {
            // Hide archived sheets (is_current_revision === false) from the tab strip by default.
            // Operators only care about current revisions when planning a cut; archived ones are
            // historical and clutter the tabs (especially after multiple revision passes).
            // The toggle below opts back in. activeTab still indexes into the full setSheets array
            // so form-data binding (handleSelectSheetById, refreshSheetData, etc.) is unchanged —
            // we just renumber the visible tabs and remap each tab's onClick to the original idx.
            const visibleSheets = setSheets
                .map((sheet, originalIdx) => ({ sheet, originalIdx }))
                .filter(({ sheet }) => showArchivedSheetsInSet || sheet.is_current_revision !== false);
            const archivedCount = setSheets.filter(s => s.is_current_revision === false).length;
            return (
                <div className="bg-white border-bottom p-2 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <ul className="nav nav-tabs mb-0">
                        <li className="nav-item">
                            <button className={`nav-link ${activeTab === 'overview' ? 'active fw-bold' : ''}`} onClick={() => handleTabChange('overview')}>Set Overview</button>
                        </li>
                        {visibleSheets.map(({ sheet, originalIdx }, visibleIdx) => (
                            <li className="nav-item" key={originalIdx}>
                                <button className={`nav-link ${activeTab === originalIdx.toString() ? 'active' : ''} ${sheet.active_status === false ? 'text-muted' : ''} ${sheet.is_current_revision === false ? 'fst-italic' : ''}`} onClick={() => handleTabChange(originalIdx.toString())}>
                                    Sheet {visibleIdx+1}
                                    {sheet.active_status === false ? ' (Inactive)' : ''}
                                    {sheet.is_current_revision === false ? ' (Archived)' : ''}
                                </button>
                            </li>
                        ))}
                    </ul>
                    {archivedCount > 0 && (
                        <div className="form-check form-check-inline small mb-0 me-2">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="showArchivedSheetsInSetToggle"
                                checked={showArchivedSheetsInSet}
                                onChange={e => setShowArchivedSheetsInSet(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="showArchivedSheetsInSetToggle">
                                Show archived ({archivedCount})
                            </label>
                        </div>
                    )}
                </div>
            );
        })()}

        <div className="p-4 overflow-auto w-100 h-100">
            {error && <div className="alert alert-danger">{error}</div>}

            {isSetMode && activeTab === 'overview' ? (
                // SET OVERVIEW
                <div className="card shadow-sm">
                    <div className="card-header bg-dark text-white">Cut Sheet Set Details</div>
                    <div className="card-body">
                        <div className="mb-3">
                            <label className="form-label">Set Name</label>
                            <input className="form-control" value={cutSheetName} onChange={e => setCutSheetName(e.target.value)} />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Description</label>
                            <textarea className="form-control" rows={3} value={description} onChange={e => setDescription(e.target.value)} />
                        </div>
                        <div className="mb-3">
                            <div className="form-check form-switch">
                                <input className="form-check-input" type="checkbox" checked={setLevelActiveStatus} onChange={(e) => setSetLevelActiveStatus(e.target.checked)} />
                                <label className="form-check-label">Active Status</label>
                            </div>
                        </div>
                        <div className="alert alert-info d-flex justify-content-between align-items-center">
                            <span>This set contains <strong>{setSheets.length}</strong> unique sheets. Configure details in tabs above.</span>
                            {setHeader.cut_sheet_set_id && (
                                <button className="btn btn-outline-secondary btn-sm" onClick={handleAddSheetToSet}>+ Add Sheet</button>
                            )}
                        </div>
                        <button className="btn btn-success btn-lg" onClick={handleSaveSet} disabled={loading}>{loading ? 'Saving...' : 'Save Entire Set'}</button>
                    </div>
                </div>
            ) : (
                // SINGLE SHEET / SHEET TAB FORM
                <>
                    {/* Header Controls (Versioning / Save) */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="d-flex align-items-center gap-2">
                            <h4 className="mb-0">
                                {isSetMode ? `Sheet ${parseInt(activeTab) + 1} Details` : (dbCutSheetId ? `Editing Sheet ${dbCutSheetId}` : "New Sheet")}
                            </h4>
                            {dbCutSheetId && <span className="badge bg-secondary">Rev {currentRevisionCode}</span>}
                            {isReadOnly && <span className="badge bg-warning text-dark">ARCHIVED</span>}
                        </div>
                        
                        <div className="d-flex gap-2">
                            {revisionHistory.length > 1 && (
                                <select className="form-select form-select-sm w-auto" onChange={e => {
                                    // FIXED: Use refresh logic if in Set Mode to prevent ejection
                                    if(isSetMode) {
                                        // Special handling for Sets: If revision changed, update this tab's ID
                                        const newId = e.target.value;
                                        setSetSheets(prev => {
                                            const c = [...prev];
                                            const idx = parseInt(activeTab);
                                            c[idx].cut_sheet_id = newId; 
                                            return c;
                                        });
                                        refreshSheetData(newId);
                                    } else {
                                        handleSelectSheetById(e.target.value);
                                    }
                                }} value={dbCutSheetId || ''}>
                                    {revisionHistory.map(rev => (
                                        <option key={rev.cut_sheet_id} value={rev.cut_sheet_id}>{rev.revision_code} {rev.is_current_revision ? '(Current)' : ''}</option>
                                    ))}
                                </select>
                            )}
                            {dbCutSheetId && !isReadOnly && (
                                <button className="btn btn-sm btn-outline-primary" onClick={handleCreateRevision}>New Version</button>
                            )}
                            {dbCutSheetId && !isSetMode && (
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => setShowDuplicate(true)}
                                    title="The same nest on other steel, e.g. when the ordered material is late">
                                    Copy onto another material…
                                </button>
                            )}
                            <DuplicateCutSheetModal show={showDuplicate} onHide={() => setShowDuplicate(false)}
                                cutSheetId={dbCutSheetId} cutSheetName={cutSheetName} materialId={materialId}
                                onCreated={(d) => { loadListItems(); handleSelectSheetById(d.cut_sheet_id); }} />
                            {!isSetMode && !isReadOnly && (
                                <button className="btn btn-primary" onClick={handleSaveSingle}>{dbCutSheetId ? "Save Changes" : "Create"}</button>
                            )}
                        </div>
                    </div>

                    {/* Obsolete Warning */}
                    {hasObsoleteParts && !isReadOnly && (
                        <div className="alert alert-danger d-flex justify-content-between align-items-center">
                            <strong>⚠ Warning: Contains obsolete parts.</strong>
                            <button className="btn btn-sm btn-danger" onClick={handleCreateRevision}>Fix (New Version)</button>
                        </div>
                    )}

                    <div className="row">
                        {/* NEW CUT SHEET CHECKLIST - Only for new sheets */}
                        {newCutSheetChecklist && (
                            <div className="col-lg-3 order-lg-last">
                                <div className="card shadow-sm sticky-top" style={{ top: '10px', zIndex: 100 }}>
                                    <div className="card-header bg-primary text-white fw-bold">
                                        <small>New Cut Sheet Setup</small>
                                    </div>
                                    <div className="card-body p-2">
                                        {/* Progress Bar */}
                                        <div className="mb-2">
                                            <div className="d-flex justify-content-between small mb-1">
                                                <span>{newCutSheetChecklist.completedCount} of {newCutSheetChecklist.totalCount}</span>
                                                <span className="fw-bold">{newCutSheetChecklist.progressPercent}%</span>
                                            </div>
                                            <div className="progress" style={{ height: '8px' }}>
                                                <div
                                                    className={`progress-bar ${newCutSheetChecklist.progressPercent === 100 ? 'bg-success' : 'bg-primary'}`}
                                                    style={{ width: `${newCutSheetChecklist.progressPercent}%` }}
                                                />
                                            </div>
                                        </div>

                                        {/* Checklist Items */}
                                        <ul className="list-unstyled mb-0 small">
                                            {newCutSheetChecklist.items.map(item => (
                                                <li
                                                    key={item.id}
                                                    className={`d-flex align-items-center py-1 px-2 rounded mb-1 ${item.completed ? 'bg-success-subtle' : 'bg-light'}`}
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => scrollToSection(item.section)}
                                                >
                                                    <span className={`me-2 ${item.completed ? 'text-success' : 'text-muted'}`}>
                                                        {item.completed ? '✓' : '○'}
                                                    </span>
                                                    <span className={item.completed ? 'text-decoration-line-through text-muted' : ''}>
                                                        {item.label}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>

                                        {newCutSheetChecklist.progressPercent === 100 && (
                                            <div className="alert alert-success py-1 px-2 mt-2 mb-0 small">
                                                ✓ Ready to save!
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* COLUMN 1: FORM */}
                        <div className={newCutSheetChecklist ? 'col-lg-6' : 'col-lg-8'}>

                            {/* Details Card */}
                            <div className="card mb-3" id="sheet-details-card">
                                <div className="card-header fw-bold">Sheet Details</div>
                                <div className="card-body">
                                    {/* Row 1: Name & Customer */}
                                    <div className="row g-3 mb-3">
                                        <div className="col-md-6">
                                            <label className={`form-label ${!cutSheetName ? 'required-field-label' : ''}`}>Name</label>
                                            <input type="text" className={`form-control ${!cutSheetName ? 'required-field' : ''}`} value={cutSheetName} onChange={e => setCutSheetName(e.target.value)} disabled={isReadOnly} />
                                        </div>
                                        <div className="col-md-6">
                                            <label className={`form-label ${!isSetMode && !customerId ? 'required-field-label' : ''}`}>Customer {isSetMode && <span className="text-muted">(Optional for Sets)</span>}</label>
                                            <select className={`form-select ${!isSetMode && !customerId ? 'required-field' : ''}`} value={customerId} onChange={e => setCustomerId(e.target.value)} disabled={isReadOnly}>
                                                <option value="">-- {isSetMode ? 'None (Mixed)' : 'Select Customer'} --</option>
                                                {allCustomers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    {/* Set Quantity (Only visible in Set Mode) */}
                                    {isSetMode && (
                                        <div className="mb-3 p-2 bg-warning-subtle border border-warning rounded">
                                            <label className="form-label fw-bold">Quantity In Set (Multiplier)</label>
                                            <div className="input-group">
                                                <span className="input-group-text">x</span>
                                                <input type="number" className="form-control" value={quantityInSet} onChange={e => setQuantityInSet(e.target.value)} min="1" />
                                            </div>
                                        </div>
                                    )}

                                    {/* Row 2: Description & Laser Time */}
                                    <div className="row g-3 mb-3">
                                        <div className="col-md-6">
                                            <label className={`form-label ${!description?.trim() ? 'required-field-label' : ''}`}>Description</label>
                                            <input type="text" className={`form-control ${!description?.trim() ? 'required-field' : ''}`} value={description} onChange={e => setDescription(e.target.value)} disabled={isReadOnly} />
                                        </div>
                                        <div className="col-md-6">
                                            <label className={`form-label ${totalCalculatedLaserTime <= 0 ? 'required-field-label' : ''}`}>Est. Laser (min)</label>
                                            <input
                                                type="number"
                                                className={`form-control bg-light ${totalCalculatedLaserTime <= 0 ? 'required-field' : ''}`}
                                                value={totalCalculatedLaserTime}
                                                readOnly
                                                title="Auto-calculated from parts list"
                                            />
                                            {historicalLaserHours > 0 ? (
                                                <small className="text-muted d-block mt-1">Historical Avg: {formatHoursAndMinutes(historicalLaserHours)}</small>
                                            ) : (
                                                <small className="text-muted d-block mt-1">No real data yet.</small>
                                            )}
                                        </div>
                                    </div>

                                    {/* Row 3: Status Toggles (Active & Flagged) */}
                                    <div className="row g-3 mb-3">
                                        <div className="col-md-6">
                                            <div className="form-check form-switch">
                                                <input className="form-check-input" type="checkbox" checked={activeStatus} onChange={(e) => setActiveStatus(e.target.checked)} disabled={isReadOnly} />
                                                <label className="form-check-label">Active Status</label>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="form-check form-switch">
                                                <input className="form-check-input" type="checkbox" checked={flagged} onChange={handleToggleFlag} disabled={isReadOnly} />
                                                <label className="form-check-label">Flagged?</label>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Flag Reason Display */}
                                    {flagged && <div className="alert alert-danger mt-2 py-1 small">Reason: {flagReason} (By: {flaggedByName})</div>}

                                    {/* Row 4: Notes (Restored) */}
                                    <div className="mb-3">
                                        <label className="form-label">Notes</label>
                                        <textarea className="form-control" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} disabled={isReadOnly} />
                                    </div>
                                </div>
                            </div>

                            {/* Material Card */}
                            <div className="card mb-3" id="material-card">
                                <div className="card-header fw-bold">Material</div>
                                <div className="card-body">
                                    <div className="p-2 mb-2 bg-light border rounded small">
                                        <strong>Filters: </strong>
                                        
                                        {/* 1. Owner Filter (Restored) */}
                                        <select className="d-inline-block w-auto me-1 mb-1 form-select-sm" value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}>
                                            <option value="">Owner (All)</option>
                                            {allCustomers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
                                        </select>

                                        {/* 2. Type Filter */}
                                        <select className="d-inline-block w-auto me-1 mb-1 form-select-sm" value={materialTypeFilter} onChange={e => setMaterialTypeFilter(e.target.value)}>
                                            <option value="">Type (All)</option>
                                            {materialTypes.map(mt => <option key={mt.material_type_id} value={mt.type_name}>{mt.type_name}</option>)}
                                        </select>

                                        {/* 3. Thickness Filter */}
                                        <select className="d-inline-block w-auto me-1 mb-1 form-select-sm" value={thicknessFilter} onChange={e => setThicknessFilter(e.target.value)}>
                                            <option value="">Thick (All)</option>
                                            {availableThicknesses.map(t => <option key={t.thickness_id} value={t.thickness_id}>{t.display_thickness}</option>)}
                                        </select>

                                        {/* 4. Finish Filter (Restored) */}
                                        <select className="d-inline-block w-auto mb-1 form-select-sm" value={surfaceFinishFilter} onChange={e => setSurfaceFinishFilter(e.target.value)}>
                                            <option value="">Finish (All)</option>
                                            {surfaceFinishes.map(sf => <option key={sf.surface_finish_id} value={sf.finish_name}>{sf.finish_name}</option>)}
                                        </select>
                                    </div>

                                    {/* Remnant toggle */}
                                    <div className="form-check form-switch mb-2">
                                      <input className="form-check-input" type="checkbox" id="useRemnantToggle" checked={useRemnant} onChange={e => { setUseRemnant(e.target.checked); setMaterialId(""); if (e.target.checked) loadRemnants(); }} disabled={isReadOnly} />
                                      <label className="form-check-label small" htmlFor="useRemnantToggle">Use Remnant</label>
                                    </div>

                                    <label className={`form-label small ${!materialId ? 'required-field-label' : ''}`}>{useRemnant ? 'Remnant' : 'Material'}</label>
                                    {useRemnant ? (
                                      <select className={`form-select mb-2 ${!materialId ? 'required-field' : ''}`} value={materialId} onChange={e => setMaterialId(e.target.value)} disabled={isReadOnly}>
                                        <option value="">-- Choose Remnant --</option>
                                        {filteredRemnants.map(r => (
                                          <option key={r.material_id} value={r.material_id}>
                                            {r.material_code} ({r.width}"x{r.length}") [{r.display_thickness} {r.surface_finish}] Qty: {r.stock_quantity}
                                          </option>
                                        ))}
                                      </select>
                                    ) : (
                                      <select className={`form-select mb-2 ${!materialId ? 'required-field' : ''}`} value={materialId} onChange={e => setMaterialId(e.target.value)} disabled={isReadOnly}>
                                        <option value="">-- Choose Material --</option>
                                        {filteredMaterials.map(m => (
                                            <option key={m.material_id} value={m.material_id}>{m.material_code} - {m.description}</option>
                                        ))}
                                      </select>
                                    )}
                                    {missingSpecWarning && <div className="text-danger small">{missingSpecWarning}</div>}
                                    
                                    <div className="row g-2 mt-2">
                                        <div className="col-md-6">
                                            <label className="form-label small fw-bold">Inventoried Drop Material</label>
                                            <select className="form-select form-select-sm" value={dropMaterialId} onChange={e => setDropMaterialId(e.target.value)} disabled={isReadOnly}>
                                                <option value="">-- None --</option>
                                                {allMaterials.map(m => <option key={m.material_id} value={m.material_id}>{m.material_code}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label small">Drop Qty</label>
                                            <input type="number" className="form-control form-control-sm" min={0} value={dropQuantity} onChange={e => setDropQuantity(e.target.value)} disabled={isReadOnly} />
                                        </div>
                                    </div>
                                    <div className="row g-2 mt-2">
                                        <div className="col-12 d-flex justify-content-between align-items-center">
                                            <label className="form-label small fw-bold mb-0" title="Width = across the grain (Amada Y), Length = with the grain (Amada X)">Non-inventory Remnants</label>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary"
                                                onClick={() => setCsRemnants(prev => [...prev, { width: "", length: "", quantity: "1" }])}
                                                disabled={isReadOnly}
                                                title="Add another remnant size"
                                            >
                                                <i className="bi bi-plus-lg me-1"></i>Add remnant
                                            </button>
                                        </div>
                                        {csRemnants.length === 0 && (
                                            <div className="col-12">
                                                <small className="text-muted fst-italic">No remnants — click "Add remnant" if this sheet leaves a leftover piece.</small>
                                            </div>
                                        )}
                                        {csRemnants.map((r, idx) => (
                                            <div key={idx} className="col-12">
                                                <div className="row g-2 align-items-center">
                                                    <div className="col">
                                                        <input
                                                            type="number" step="0.01" min="0"
                                                            className="form-control form-control-sm"
                                                            placeholder="W across grain"
                                                            title="Width (in): across the grain, Amada Y"
                                                            value={r.width}
                                                            onChange={e => setCsRemnants(prev => prev.map((it, i) => i === idx ? { ...it, width: e.target.value } : it))}
                                                            disabled={isReadOnly}
                                                        />
                                                    </div>
                                                    <div className="col-auto text-muted small">×</div>
                                                    <div className="col">
                                                        <input
                                                            type="number" step="0.01" min="0"
                                                            className="form-control form-control-sm"
                                                            placeholder="L with grain"
                                                            title="Length (in): with the grain, Amada X"
                                                            value={r.length}
                                                            onChange={e => setCsRemnants(prev => prev.map((it, i) => i === idx ? { ...it, length: e.target.value } : it))}
                                                            disabled={isReadOnly}
                                                        />
                                                    </div>
                                                    <div className="col-auto text-muted small">@</div>
                                                    <div className="col">
                                                        <input
                                                            type="number" step="1" min="1"
                                                            className="form-control form-control-sm"
                                                            placeholder="Qty per sheet"
                                                            value={r.quantity}
                                                            onChange={e => setCsRemnants(prev => prev.map((it, i) => i === idx ? { ...it, quantity: e.target.value } : it))}
                                                            disabled={isReadOnly}
                                                        />
                                                    </div>
                                                    {amadaSizeText(r.width, r.length) ? (
                                                        <div className="col-auto text-muted small" title="Amada lists X (with the grain) first">
                                                            Amada {amadaSizeText(r.width, r.length)}
                                                        </div>
                                                    ) : null}
                                                    <div className="col-auto">
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => setCsRemnants(prev => prev.filter((_it, i) => i !== idx))}
                                                            disabled={isReadOnly}
                                                            title="Remove this remnant"
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </button>
                                                    </div>
                                                </div>
                                                {(() => {
                                                    // A remnant keeps its sheet's grain: it must fit the sheet the same way round.
                                                    const sheet = allMaterials.find(m => m.material_id === parseInt(materialId));
                                                    const g = sheet ? grainCheck(r.width, r.length, sheet.width, sheet.length) : null;
                                                    if (!g) return null;
                                                    return (
                                                        <div className={`small mt-1 ${g.code === 'looks_swapped' ? 'text-warning-emphasis' : 'text-danger'}`}>
                                                            <i className="bi bi-arrow-left-right me-1"></i>{g.message}
                                                            {g.code === 'looks_swapped' && !isReadOnly && (
                                                                <button type="button" className="btn btn-sm btn-link py-0"
                                                                    onClick={() => setCsRemnants(prev => prev.map((it, i) => i === idx ? { ...it, width: String(g.suggestedWidth), length: String(g.suggestedLength) } : it))}>
                                                                    Swap to {g.suggestedWidth} x {g.suggestedLength}
                                                                </button>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                        ))}
                                        {csRemnants.length > 0 && (
                                            <div className="col-12">
                                                <small className="text-muted">Each remnant is auto-added to remnant inventory every time this sheet is cut.</small>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* MATERIAL COST MATH — read-only breakdown of exactly how the
                                per-part "From Cut Sheet" prices are computed (price source,
                                remnant credit, area-weighted allocation). Data comes from
                                GET /cut_sheets/{id}.cost_breakdown; refreshed on load + save. */}
                            {dbCutSheetId && costBreakdown && (
                                <div className="card mb-3">
                                    <div className="card-header fw-bold d-flex justify-content-between align-items-center">
                                        <span>Material Cost Math</span>
                                        {costBreakdown.price && (
                                            <span className={`badge ${costBreakdown.price.source === 'cwt_rule' ? 'bg-info text-dark' : 'bg-secondary'}`}
                                                  title={costBreakdown.price.source === 'cwt_rule'
                                                      ? 'Sheet price derives from a CWT pricing rule (weight × $/CWT ÷ 100)'
                                                      : 'Sheet price comes from the preferred vendor record (not CWT-derived)'}>
                                                {costBreakdown.price.source === 'cwt_rule' ? 'CWT-priced' : 'Vendor price'}
                                            </span>
                                        )}
                                    </div>
                                    <div className="card-body small">
                                        {!costBreakdown.ok && (
                                            <div className="alert alert-warning py-1 px-2 mb-2">
                                                {costBreakdown.reason === 'no_material' && 'No material selected — no cost math to show.'}
                                                {costBreakdown.reason === 'no_vendor_cost' && 'The selected material has no vendor price, so part costs cannot be calculated.'}
                                                {costBreakdown.reason === 'no_parts' && 'No parts saved on this sheet yet.'}
                                                {costBreakdown.reason === 'zero_part_area' && 'Parts are missing area data (no solid profile and no L×W) — costs cannot be allocated.'}
                                            </div>
                                        )}
                                        {costBreakdown.price && (
                                            <>
                                                <div className="d-flex justify-content-between align-items-baseline">
                                                    <span>
                                                        Full sheet price
                                                        {costBreakdown.price.vendor_name ? <> — <strong>{costBreakdown.price.vendor_name}</strong></> : null}
                                                        {costBreakdown.price.price_updated_at ? <span className="text-muted"> (as of {new Date(costBreakdown.price.price_updated_at).toLocaleDateString()})</span> : null}
                                                    </span>
                                                    <span className="font-monospace fw-bold">${Number(costBreakdown.price.sheet_cost).toFixed(2)}</span>
                                                </div>
                                                {costBreakdown.price.cwt && costBreakdown.price.cwt.matches_price && (
                                                    <div className="text-muted ms-3">
                                                        = {Number(costBreakdown.material.sheet_weight_lbs).toFixed(1)} lb × ${Number(costBreakdown.price.cwt.cwt_price).toFixed(2)}/CWT ÷ 100
                                                        {costBreakdown.price.cwt.vendor_specific_rule ? ' (vendor-specific CWT rule)' : ' (default CWT rule)'}
                                                    </div>
                                                )}
                                                {costBreakdown.price.cwt && !costBreakdown.price.cwt.matches_price && (
                                                    <div className="text-muted ms-3">
                                                        Linked CWT rule computes ${Number(costBreakdown.price.cwt.derived_sheet_cost).toFixed(2)} — vendor price differs (manually set or awaiting the nightly price update).
                                                    </div>
                                                )}

                                                {costBreakdown.remnants && costBreakdown.remnants.length > 0 && !costBreakdown.remnant_dims_missing && costBreakdown.material.sheet_area_sq_in ? (
                                                    <>
                                                        <div className="d-flex justify-content-between align-items-baseline mt-1 text-danger">
                                                            <span>
                                                                − Remnant credit: {costBreakdown.remnants.map(r => `${r.width}" × ${r.length}"${r.quantity > 1 ? ` ×${r.quantity}` : ''}`).join(' + ')}
                                                                {' '}= {Number(costBreakdown.remnant_area_sq_in).toFixed(1)} in²
                                                                {' '}({(100 * (1 - costBreakdown.usable_fraction)).toFixed(1)}% of the {Number(costBreakdown.material.sheet_area_sq_in).toFixed(0)} in² sheet)
                                                            </span>
                                                            <span className="font-monospace fw-bold">−${Number(costBreakdown.remnant_credit).toFixed(2)}</span>
                                                        </div>
                                                        <hr className="my-1" />
                                                        <div className="d-flex justify-content-between align-items-baseline">
                                                            <span className="fw-bold">Allocated to parts ({(100 * costBreakdown.usable_fraction).toFixed(1)}% of sheet)</span>
                                                            <span className="font-monospace fw-bold">${Number(costBreakdown.allocatable_sheet_cost).toFixed(2)}</span>
                                                        </div>
                                                        <div className="text-muted">The remnant returns to remnant inventory carrying the ${Number(costBreakdown.remnant_credit).toFixed(2)} credit — parts only pay for the sheet they consume.</div>
                                                    </>
                                                ) : (
                                                    !costBreakdown.remnant_dims_missing && (
                                                        <div className="text-muted mt-1">No remnants defined — the full sheet price is allocated to the parts.</div>
                                                    )
                                                )}
                                                {costBreakdown.remnant_dims_missing && (
                                                    <div className="alert alert-warning py-1 px-2 my-1">
                                                        Remnants are defined, but the material record has no width/length — the remnant credit cannot be computed, so the FULL sheet price is allocated to parts.
                                                    </div>
                                                )}

                                                {costBreakdown.ok && (
                                                    <>
                                                        <div className="text-muted mt-2">
                                                            Per part: area × (${Number(costBreakdown.allocatable_sheet_cost).toFixed(2)} ÷ {Number(costBreakdown.total_nested_area_sq_in).toFixed(1)} in² nested) = ${Number(costBreakdown.cost_per_sq_in).toFixed(4)}/in²
                                                        </div>
                                                        <table className="table table-sm mt-1 mb-1">
                                                            <thead>
                                                                <tr>
                                                                    <th>Part</th>
                                                                    <th className="text-end">Area (in²)</th>
                                                                    <th className="text-end">Qty/Sheet</th>
                                                                    <th className="text-end">Share</th>
                                                                    <th className="text-end">Cost each</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {costBreakdown.parts.map(p => (
                                                                    <tr key={p.cut_sheet_part_id}>
                                                                        <td>
                                                                            {p.product_id}
                                                                            {p.area_source === 'bounding_box' && <span className="text-warning" title="Part has no solid-profile area — bounding box (L×W) used"> *</span>}
                                                                        </td>
                                                                        <td className="text-end font-monospace">{Number(p.area_sq_in).toFixed(2)}</td>
                                                                        <td className="text-end font-monospace">{p.quantity_per_sheet}</td>
                                                                        <td className="text-end font-monospace">{(100 * p.share_of_sheet).toFixed(1)}%</td>
                                                                        <td className="text-end font-monospace fw-bold">${Number(p.cost_per_part).toFixed(2)}</td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                            <tfoot>
                                                                <tr className="fw-bold">
                                                                    <td>Total</td>
                                                                    <td className="text-end font-monospace">{Number(costBreakdown.total_nested_area_sq_in).toFixed(1)}</td>
                                                                    <td className="text-end font-monospace">{costBreakdown.parts.reduce((s, p) => s + p.quantity_per_sheet, 0)}</td>
                                                                    <td className="text-end font-monospace">100%</td>
                                                                    <td className="text-end font-monospace">${costBreakdown.parts.reduce((s, p) => s + p.cost_per_part * p.quantity_per_sheet, 0).toFixed(2)}</td>
                                                                </tr>
                                                            </tfoot>
                                                        </table>
                                                        {costBreakdown.parts.some(p => p.area_source === 'bounding_box') && (
                                                            <div className="text-muted">* no solid-profile area on the part — bounding box (L×W) used instead.</div>
                                                        )}
                                                        <div className="text-muted fst-italic">Recalculated on save and by the nightly price update — unsaved edits above are not reflected yet.</div>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* COLUMN 2: IMAGE & DATA */}
                        <div className={newCutSheetChecklist ? 'col-lg-3' : 'col-lg-4'}>
                            <div className="card mb-3 sticky-top" style={{top: '10px', zIndex: 100}}>
                                <div className="card-header fw-bold">Image</div>
                                <div className="card-body text-center" onPaste={handlePaste}>
                                    {imageUrl ? 
                                        <img src={imageUrl} className="img-thumbnail mb-2 dark-invertible" style={{cursor: 'pointer'}} onClick={() => setEnlargedImage(imageUrl)} /> 
                                        : <div className="text-muted border py-5 mb-2 rounded bg-light">No Image</div>
                                    }
                                    <input type="file" className="form-control form-control-sm" onChange={e => {
                                        if(e.target.files[0]) {
                                            setUploadFile(e.target.files[0]);
                                            const reader = new FileReader();
                                            reader.onload = (ev) => {
                                                // Preview
                                                const img = document.createElement('img');
                                                img.src = ev.target.result;
                                            };
                                            reader.readAsDataURL(e.target.files[0]);
                                        }
                                    }} disabled={isReadOnly} />
                                    <button className="btn btn-sm btn-secondary w-100 mt-2" onClick={handleUploadImage} disabled={!uploadFile || isReadOnly}>Upload</button>
                                    {uploadFile && (
                                        <div className="mt-2 text-start p-2 bg-light border rounded">
                                            <small className="d-block text-muted fw-bold mb-1">Preview (Unsaved):</small>
                                            <img src={URL.createObjectURL(uploadFile)} alt="Pending" className="img-thumbnail" style={{ maxHeight: "150px" }} />
                                        </div>
                                    )}

                                    <small className="text-muted d-block mt-1">Paste (Ctrl+V) supported.</small>
                                </div>
                            </div>

                            <div className="card mb-3" id="amada-times-card">
                                <div className="card-header fw-bold">Amada Times</div>
                                <div className="card-body">
                                    <div className="row g-2">
                                        {/* Cut Path Time */}
                                        <div className="col-3">
                                            <label className={`small ${!hasTimeValue(cutPathTime) ? 'required-field-label' : ''}`}>Cut Path</label>
                                            <input
                                                className={`form-control form-control-sm ${!hasTimeValue(cutPathTime) ? 'required-field' : ''}`}
                                                value={cutPathTime}
                                                onChange={e => setCutPathTime(e.target.value)}
                                                onBlur={e => handleTimeBlur(setCutPathTime, e.target.value)}
                                                disabled={isReadOnly}
                                            />
                                        </div>

                                        {/* SH Cut Time */}
                                        <div className="col-3">
                                            <label className={`small ${!hasTimeValue(shCutTime) ? 'required-field-label' : ''}`}>SH Cut</label>
                                            <input
                                                className={`form-control form-control-sm ${!hasTimeValue(shCutTime) ? 'required-field' : ''}`}
                                                value={shCutTime}
                                                onChange={e => setShCutTime(e.target.value)}
                                                onBlur={e => handleTimeBlur(setShCutTime, e.target.value)}
                                                disabled={isReadOnly}
                                            />
                                        </div>

                                        {/* Pierce Time */}
                                        <div className="col-3">
                                            <label className={`small ${!hasTimeValue(pierceTime) ? 'required-field-label' : ''}`}>Pierce</label>
                                            <input
                                                className={`form-control form-control-sm ${!hasTimeValue(pierceTime) ? 'required-field' : ''}`}
                                                value={pierceTime}
                                                onChange={e => setPierceTime(e.target.value)}
                                                onBlur={e => handleTimeBlur(setPierceTime, e.target.value)}
                                                disabled={isReadOnly}
                                            />
                                        </div>

                                        {/* Shuttle Time */}
                                        <div className="col-3">
                                            <label className="small">Shuttle</label>
                                            <input
                                                className="form-control form-control-sm"
                                                value={shuttleTime}
                                                onChange={e => setShuttleTime(e.target.value)}
                                                onBlur={e => handleTimeBlur(setShuttleTime, e.target.value)}
                                                disabled={isReadOnly}
                                                title="Shuttle table swap time per sheet (default 60s)"
                                            />
                                        </div>

                                        {/* Total Time (Read Only) */}
                                        <div className="col-12">
                                            <label className={`small fw-bold ${!hasTimeValue(totalCutTime) ? 'required-field-label' : ''}`}>Total Cut Time (Auto-Calc)</label>
                                            <input
                                                className={`form-control form-control-sm bg-light fw-bold ${!hasTimeValue(totalCutTime) ? 'required-field' : ''}`}
                                                value={totalCutTime}
                                                readOnly
                                                title="Sum of Cut Path + SH Cut + Pierce (excludes shuttle)"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* PARTS TABLE (Full Width) */}
                    <div className="card mb-3" id="parts-card">
                        <div className={`card-header fw-bold d-flex justify-content-between ${cutSheetParts.length === 0 ? 'bg-danger-subtle border-danger' : ''}`}>
                            <span className={cutSheetParts.length === 0 ? 'required-field-label' : ''}>Parts</span>
                            {/* AUTO-CALC RESTORED */}
                            {totalCalculatedLaserTime > 0 && <span className="badge bg-info text-dark">Calc Time: {totalCalculatedLaserTime} min</span>}
                        </div>
                        <div className="card-body">
                            {(() => {
                                const badAreaParts = cutSheetParts.filter(p =>
                                    (p.stock_type == null || p.stock_type === 'Sheet Stock') &&
                                    (p.solid_profile_area == null || Number(p.solid_profile_area) === 0)
                                );
                                if (badAreaParts.length === 0) return null;
                                return (
                                    <div className="alert alert-warning fw-bold mb-3">
                                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                        {badAreaParts.length === 1 ? 'This part has' : `${badAreaParts.length} parts have`} a profile area of 0 — DXF analysis likely failed (lead-in lines often break this). Material cost will fall back to the bounding box. Fix on the Part page for accurate costing:
                                        <ul className="mb-0 mt-1">
                                            {badAreaParts.map(p => (
                                                <li key={p.part_id}><strong>{p.product_id || p.part_id}</strong>{p.part_description ? ` — ${p.part_description}` : ''}</li>
                                            ))}
                                        </ul>
                                    </div>
                                );
                            })()}
                            <table className="table table-sm table-bordered table-hover">
                                <thead className="table-light">
                                    <tr><th>Part</th><th>Desc</th><th>Qty</th><th>Est Time</th><th>Mat Cost</th><th>Actions</th></tr>
                                </thead>
                                <tbody>
                                    {cutSheetParts.length === 0 ? <tr><td colSpan="6" className="text-center text-muted">No parts</td></tr> :
                                    cutSheetParts.map((p, i) => {
                                        const badArea = (p.stock_type == null || p.stock_type === 'Sheet Stock') && (p.solid_profile_area == null || Number(p.solid_profile_area) === 0);
                                        return (
                                        <tr key={i} className={badArea ? 'table-warning' : ''}>
                                            <td>{p.product_id || p.part_id}{badArea && <i className="bi bi-exclamation-triangle-fill text-warning ms-1" title="Profile area is 0 — DXF analysis failed"></i>}</td>
                                            <td>{p.part_description}</td>
                                            <td>{p.quantity_per_sheet}</td>
                                            <td>{p.initial_est_laser_minutes || 0}</td>
                                            <td>{p.material_cost ? `$${Number(p.material_cost).toFixed(2)}` : '-'}</td>
                                            <td><button className="btn btn-xs btn-outline-danger" onClick={() => handleDeletePart(p.part_id, i)} disabled={isReadOnly}>Del</button></td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr><td colSpan="6" className="text-end">
                                        {/* RESTORED: Recalc Button */}
                                        <button className="btn btn-sm btn-secondary" onClick={handleCalculateMaterialCosts} disabled={!dbCutSheetId || isReadOnly}>Recalc Costs</button>
                                    </td></tr>
                                </tfoot>
                            </table>

                            <div className="bg-light p-3 border rounded">
                                <h6 className="small fw-bold">Add Part</h6>
                                <div className="row g-2 align-items-end">
                                    <div className="col-md-3">
                                        <label className="small">Filter Customer</label>
                                        <select className="form-select form-select-sm" value={partCustomerFilter} onChange={e => setPartCustomerFilter(e.target.value)} disabled={isReadOnly}>
                                            <option value="">All</option>
                                            {allCustomers.map(c => <option key={c.customer_id} value={c.customer_id}>{c.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="col-md-5">
                                        <label className="small">Search Part</label>
                                        <input className="form-control form-control-sm" list="part-options" value={newPartSearch} onChange={e => {
                                            setNewPartSearch(e.target.value);
                                            const match = filteredPartsForDropdown.find(p => `${p.product_id} - ${p.description}` === e.target.value);
                                            if(match) setNewPartId(match.part_id);
                                        }} disabled={isReadOnly} placeholder="Type to search..." />
                                        <datalist id="part-options">
                                            {filteredPartsForDropdown.map(p => <option key={p.part_id} value={`${p.product_id} - ${p.description}`} />)}
                                        </datalist>
                                    </div>
                                    <div className="col-md-2">
                                        <label className="small">Qty</label>
                                        <input type="number" className="form-control form-control-sm" value={newPartQty} onChange={e => setNewPartQty(e.target.value)} disabled={isReadOnly} />
                                    </div>
                                    <div className="col-md-2">
                                        <button className="btn btn-sm btn-success w-100" onClick={handleAddPart} disabled={isReadOnly}>Add</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Recommended Packaging (V2 — operator override editor) */}
                    <div className="card mb-3" id="packaging-card">
                        <div className="card-header fw-bold">
                            <i className="bi bi-box-seam me-1"></i>Recommended Packaging
                            <span className="text-muted small ms-2">(advisory — drives the laser kiosk display)</span>
                        </div>
                        <div className="card-body">
                            <CutSheetPackagingEditor
                                cutSheetId={dbCutSheetId}
                                apiBase={API_BASE}
                                isReadOnly={isReadOnly}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
      </div>

      {/* Image Modal */}
      {enlargedImage && (
        <div className="modal d-block" style={{ backgroundColor: "rgba(0,0,0,0.8)" }} onClick={() => setEnlargedImage(null)}>
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content">
                    <div className="modal-body p-0 text-center">
                        <img src={enlargedImage} className="img-fluid" alt="Full" />
                    </div>
                </div>
            </div>
        </div>
      )}

      {/* Amada Multi-Sheet Import Modal */}
      <AmadaImportModal
        show={showAmadaImport}
        onClose={() => setShowAmadaImport(false)}
        allCustomers={allCustomers}
        allMaterials={allMaterials}
        allParts={allParts}
        onImported={() => {
            setShowAmadaImport(false);
            // Refresh the list so the newly created cut sheets appear immediately
            loadListItems();
        }}
      />
    </div>
  );
}